// mood.js
import { showDisplayedArtwork } from './artViewer.js';
import { getAuthToken } from './auth.js';
import { apiBaseUrl, apiBaseSocket } from '../../backend/config.js';
import io from 'socket.io-client';
import { t, confirmT, updateElementText } from './translationHelper.js';
// Create a completely new WebSocket connection for the Mood Board
let moodSocket = null;
const sessionId = localStorage.getItem("sessionId");
const storedSocketId = localStorage.getItem("socketId");
const authToken = getAuthToken(); 

let userMoodScore = 0;
let marketMoodScore = 0;
let p5Instance = null;
let updateTimer = null;
let lastTransactionTime = null;
let lastTransactionMood = 0;
let burstCells = [];
let cols = 0;
let rows = 0;
let cellSize = 10;
const MOOD_AMPLIFICATION_FACTOR = 1.8;

let moodSocketSetupComplete = false;
let socketSetupComplete = false;

let isConnected = false;
let reconnectAttempts = 0;
const MAX_RECONNECT_ATTEMPTS = 5;

function connectMoodSocket() {
  const token = getAuthToken() || localStorage.getItem('token');
  const sessionId = localStorage.getItem('sessionId');

  if (!token || !sessionId) {
	console.error('❌ Missing token or sessionId. Cannot connect mood socket.');
	return;
  }

  moodSocket = io(apiBaseSocket, {
	query: {
	  token,
	  userId: sessionId,
	  sessionId,
	  page: 'moodBoard' // This is critical - ensures server knows this is a mood board client
	},
	transports: ['websocket']
  });

  moodSocket.on('connect', () => {
	console.log('✅ Connected to mood board socket:', moodSocket.id);
	localStorage.setItem('moodSocketId', moodSocket.id);
	moodSocket.emit('registerMoodBoard', { 
	  moodSocketId: moodSocket.id,
	  sessionId: sessionId 
	});
  });

  moodSocket.on('disconnect', (reason) => {
	console.warn(`❌ Mood board socket disconnected: ${reason}`);
  });

  moodSocket.on('connect_error', (err) => {
	console.error('❌ Connection error:', err);
  });

  moodSocket.on('transactionBurst', (data) => {
	console.log('🎆 Received transactionBurst:', data);
	if (!data) return;
	let moodScore = Number(data.moodScore);
	if (!isNaN(moodScore)) {
	  triggerBurst(moodScore);
	} else {
	  console.warn("⚠️ Invalid moodScore in transactionBurst:", data.moodScore);
	}
  });
}

function handleReconnect() {
  if (reconnectAttempts < MAX_RECONNECT_ATTEMPTS) {
	reconnectAttempts++;
	console.log(`🔄 Reattempting connection (${reconnectAttempts}/${MAX_RECONNECT_ATTEMPTS})...`);
	setTimeout(connectMoodSocket, 2000);
  } else {
	console.error(`❌ Maximum reconnection attempts (${MAX_RECONNECT_ATTEMPTS}) reached.`);
  }
}


function setupMoodBoardListeners() {
  if (moodSocketSetupComplete) {
	console.log("🎧 Mood Board listeners already set up. Skipping.");
	return;
  }

  console.log("✅ Setting up Mood Board WebSocket listeners.");

  // Check if we're using socket.io or native WebSockets
  if (moodSocket && typeof moodSocket.on === 'function') {
	// Socket.io style already handled in connectMoodSocket
	console.log("✅ Using Socket.io style listeners (already configured).");
  } else if (moodSocket) {
	// Native WebSocket API
	moodSocket.onmessage = (event) => {
	  try {
		const data = JSON.parse(event.data);
		console.log("💬 Mood Board WebSocket received message:", data);
	
		if (data.type === "authenticated") {
		  console.log("✅ WebSocket authentication successful!");
		} else if (data.type === "burstMessage") {
		  handleBurstMessage(data);
		}
	
	  } catch (err) {
		console.error("⚠️ Error processing Mood Board WebSocket message:", err);
	  }
	};
  } else {
	console.error("❌ Cannot set up listeners - moodSocket is not initialized.");
  }

  moodSocketSetupComplete = true;
}

function ensureBurstListener() {
  if (socketSetupComplete) {
	console.log("🎧 Burst listener already set up. Skipping.");
	return;
  }

  console.log("✅ Setting up burst listener.");
  setupBurstListener();
  socketSetupComplete = true;
	moodSocketSetupComplete = true;
  }

async function loadP5() {
  if (!window.p5) {
	const script = document.createElement("script");
	script.src = "https://cdnjs.cloudflare.com/ajax/libs/p5.js/1.4.0/p5.min.js";
	script.onload = () => initP5Sketch(); // Call initP5Sketch after loading
	document.body.appendChild(script);
  } else {
	initP5Sketch();// Initialize if p5 is already loaded
  }
}

function initP5Sketch() {
  const container = document.getElementById('moodCanvas');
  if (!container) {
	console.error("❌ moodCanvas not found. Aborting p5.js initialization.");
	return;
  }
  if (p5Instance && typeof p5Instance.remove === 'function') {
	console.log("🛑 Removing previous p5 instance...");
	p5Instance.remove();
  }
  console.log("✅ moodCanvas found. Initializing p5 sketch...");
  p5Instance = new window.p5(pixelMoodArt, "moodCanvas");
}

function setupBurstListener() {
  if (socketSetupComplete) {
	console.log("🎧 Burst listener already set up. Skipping.");
	return;
  }

  console.log("✅ Setting up burst listener.");

  document.addEventListener("transactionBurst", (event) => {
	const data = event.detail;
	console.log('🎆 TRANSACTION BURST EVENT RECEIVED:', JSON.stringify(data));
	if (!data) {
	  console.error("⚠️ Empty transaction burst data received");
	  return;
	}

	const { moodScore, transactionId } = data;
	if (typeof moodScore === "number") {
	  console.log(`🔥 Triggering burst with mood score: ${moodScore} for transaction: ${transactionId}`);
	  triggerBurst(moodScore);
	} else {
	  console.warn("⚠️ Invalid mood score in transaction burst event:", data);
	}
  });

  document.addEventListener("bidAcceptSuccess", (event) => {
	const data = event.detail;
	console.log("📣 Bid accept success received:", data);
	if (data.moodResult && typeof data.moodResult.transactionMoodScore === "number") {
	  console.log("📊 Transaction mood score from acceptance:", data.moodResult.transactionMoodScore);
	  triggerBurst(data.moodResult.transactionMoodScore);
	}
  });

  document.addEventListener("bidDeclineSuccess", (event) => {
	const data = event.detail;
	console.log("📣 Bid decline success received:", data);
	if (data.moodResult && typeof data.moodResult.transactionMoodScore === "number") {
	  console.log("📊 Transaction mood score from decline:", data.moodResult.transactionMoodScore);
	  triggerBurst(data.moodResult.transactionMoodScore);
	}
  });

  socketSetupComplete = true;
  console.log("✅ Burst listener setup complete.");
}

async function initializeBoard() {
  // Establish the WebSocket connection only once.
if (!moodSocket || (typeof moodSocket.connected !== 'undefined' && !moodSocket.connected)) {
	console.log("🔌 Establishing WebSocket connection...");
	connectMoodSocket();
  }

  ensureBurstListener();
  await loadP5();
  await initMoodPage();
  // Start periodic mood data updates (e.g., every 15 seconds).
  if (updateTimer) clearInterval(updateTimer);
  updateTimer = setInterval(initMoodPage, 15000);
}

async function initMoodPage() {
  try {
	console.log("📌 Refreshing Mood Page Data...");
	sessionId = localStorage.getItem("sessionId");

	if (!sessionId) {
	  console.error("❌ No session ID found in localStorage. Are you logged in?");
	  showNotification("Session ID is missing. Please log in.", false);
	  return;
	}

	console.log("✅ Session ID retrieved:", sessionId);
	const traderMood = await fetchTraderMood(sessionId);
	const marketMood = await fetchMarketMood(sessionId);

	// Log raw responses for debugging.
	console.log("📊 Raw market mood response:", marketMood);

	// (Optional) Store previous values for logging.
	const oldUserMoodScore = userMoodScore;
	const oldMarketMoodScore = marketMoodScore;

	// --- CALCULATE USER MOOD ---
	if (traderMood) {
	  if (typeof traderMood.averageMood !== "undefined" && !isNaN(parseFloat(traderMood.averageMood))) {
		userMoodScore = parseFloat(traderMood.averageMood);
		console.log("📊 Using API-provided user mood:", userMoodScore);
	  } else if (traderMood.details?.moodHistory && traderMood.details.moodHistory.length > 0) {
		userMoodScore = calculateWeightedMood(traderMood.details.moodHistory);
		console.log("📊 Calculated user mood from details.moodHistory:", userMoodScore);
	  } else if (traderMood.moodHistory && traderMood.moodHistory.length > 0) {
		userMoodScore = calculateWeightedMood(traderMood.moodHistory);
		console.log("📊 Calculated user mood from moodHistory:", userMoodScore);
	  } else {
		console.warn("⚠️ No valid user mood data in API response");
		userMoodScore = isNaN(userMoodScore) || userMoodScore === 0 ? 0.25 : userMoodScore;
	  }
	} else {
	  console.warn("⚠️ Failed to retrieve trader mood data");
	  userMoodScore = isNaN(userMoodScore) || userMoodScore === 0 ? 0.25 : userMoodScore;
	}

	// Apply the amplification factor to the user mood.
	userMoodScore *= MOOD_AMPLIFICATION_FACTOR;
	console.log("📊 Amplified user mood:", userMoodScore);

	// --- CALCULATE MARKET MOOD ---
	if (marketMood) {
	  if (typeof marketMood.averageMood !== "undefined" && !isNaN(parseFloat(marketMood.averageMood))) {
		marketMoodScore = parseFloat(marketMood.averageMood);
		console.log("📊 Base market mood from API:", marketMoodScore);
		marketMoodScore *= MOOD_AMPLIFICATION_FACTOR;
		console.log("📊 Amplified market mood:", marketMoodScore);
	  } else {
		console.warn("⚠️ No valid market mood average in API response");
		marketMoodScore = isNaN(marketMoodScore) ? -1.8 * MOOD_AMPLIFICATION_FACTOR : marketMoodScore;
	  }
	} else {
	  console.warn("⚠️ Failed to retrieve market mood data");
	  marketMoodScore = isNaN(marketMoodScore) ? -1.8 * MOOD_AMPLIFICATION_FACTOR : marketMoodScore;
	}

	// Ensure mood scores are finite numbers.
	userMoodScore = isFinite(userMoodScore) ? userMoodScore : 0.25 * MOOD_AMPLIFICATION_FACTOR;
	marketMoodScore = isFinite(marketMoodScore) ? marketMoodScore : -0.15 * MOOD_AMPLIFICATION_FACTOR;

	// Update UI elements.
	if (traderMood) displayTraderMood(traderMood);
	if (marketMood) displayMarketMood(marketMood);
	displayMoodScores();

	console.log("✅ Final mood values:", {
	  userMoodScore: userMoodScore.toFixed(3),
	  marketMoodScore: marketMoodScore.toFixed(3)
	});

  } catch (error) {
	console.error("❌ Error in initMoodPage:", error);
	userMoodScore = isNaN(userMoodScore) ? 0.25 * MOOD_AMPLIFICATION_FACTOR : userMoodScore;
	marketMoodScore = isNaN(marketMoodScore) ? -0.15 * MOOD_AMPLIFICATION_FACTOR : marketMoodScore;
	displayMoodScores();
	showNotification("Error loading mood data. Using fallback values.", false);
  }
}

function calculateWeightedMood(moodHistory) {
  if (!moodHistory || !Array.isArray(moodHistory) || moodHistory.length === 0) {
	console.warn("⚠️ Invalid mood history data");
	return 0;
  }
  
  // Process and validate entries
  const validEntries = moodHistory
	.filter(entry => entry && (
	  typeof entry.positiveImpact !== 'undefined' || 
	  typeof entry.negativeImpact !== 'undefined' ||
	  typeof entry.value !== 'undefined'
	))
	.map(entry => ({
	  positiveImpact: parseFloat(entry.positiveImpact) || 0,
	  negativeImpact: parseFloat(entry.negativeImpact) || 0,
	  value: parseFloat(entry.value) || (entry.positiveImpact - entry.negativeImpact) || 0,
	  timestamp: entry.timestamp || new Date().toISOString()
	}));
  
  if (validEntries.length === 0) {
	console.warn("⚠️ No valid entries in mood history");
	return 0;
  }
  
  // Sort by timestamp (newest first) and take most recent 5
  const recentHistory = [...validEntries]
	.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
	.slice(0, 5);
  
  console.log("📊 Recent mood history entries for calculation:", recentHistory.length);
  
  // Apply weighted calculation with decaying importance
  let weightedMood = 0;
  let totalWeight = 0;
  
  recentHistory.forEach((entry, index) => {
	const weight = Math.pow(2, 4 - index); // Exponential decay: 16, 8, 4, 2, 1
	const entryMood = entry.value || (entry.positiveImpact - entry.negativeImpact);
	
	console.log(`Entry mood ${index}: ${entryMood} with weight ${weight}`);
	weightedMood += entryMood * weight;
	totalWeight += weight;
  });
  
  const result = totalWeight > 0 ? weightedMood / totalWeight : 0;
  console.log(`Weighted mood calculation result: ${result}`);
  return result;
}

async function fetchTraderMood(sessionId) {
  if (!sessionId) {
	sessionId = localStorage.getItem("sessionId"); // Fallback
  }

  if (!sessionId) {
	console.error("❌ Missing sessionId in localStorage!");
	showNotification("Error: No session ID found", false);
	return null;
  }

  try {
	console.log("📊 Fetching trader mood with sessionId:", sessionId);
	const startTime = Date.now();

	const response = await fetch(`${apiBaseUrl}/api/mood/trader/${sessionId}`, {
	  headers: {
		'Content-Type': 'application/json',
		'Authorization': `Bearer ${getAuthToken()}`
	  },
	  cache: 'no-store', // Prevent caching
	});

	const elapsedTime = Date.now() - startTime;
	console.log(`📊 Trader mood API response time: ${elapsedTime}ms`);

	if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

	// Parse the JSON response
	const data = await response.json();
	console.log("📊 Trader mood data received:", data);
	
	// Verify the data structure
	if (typeof data.averageMood === 'undefined') {
	  console.warn("⚠️ Trader mood response missing averageMood field");
	}
	
	return data;
  } catch (error) {
	console.error('Error fetching trader mood:', error);
	showNotification(`Error fetching trader mood: ${error.message}`, false);
	return null;
  }
}

function displayTraderMood(traderMood) {
  if (!traderMood) return;
  
  const traderMoodElement = document.getElementById('traderMood');
  if (traderMoodElement) {
	// Check each possible location for averageMood in the API response
	let moodValue;
	
	if (traderMood.averageMood !== undefined && !isNaN(parseFloat(traderMood.averageMood))) {
	  moodValue = parseFloat(traderMood.averageMood);
	} else if (traderMood.details?.averageMood !== undefined && !isNaN(parseFloat(traderMood.details.averageMood))) {
	  moodValue = parseFloat(traderMood.details.averageMood);
	} else {
	  moodValue = null;
	}
	
	if (moodValue !== null) {
	  traderMoodElement.textContent = `Your Mood: ${moodValue.toFixed(2)}`;
	  traderMoodElement.style.color = getMoodTextColor(moodValue);
	} else {
	  traderMoodElement.textContent = `Your Mood: N/A`;
	  traderMoodElement.style.color = getMoodTextColor(0);
	}
  }
  
  // Display baseline factors if available
  if (traderMood.baseline || traderMood.details?.baseline) {
	displayMoodFactors(traderMood.baseline || traderMood.details?.baseline);
  }
}

async function fetchMarketMood(sessionId) {
  if (!sessionId) {
	sessionId = localStorage.getItem("sessionId"); // Fallback
  }

  if (!sessionId) {
	console.error("❌ Error: Missing sessionId in localStorage!");
	showNotification("Error: No session ID found", false);
	return null;
  }

  try {
	console.log(`📊 Fetching market mood with sessionId: ${sessionId}`);
	const startTime = Date.now();

	const response = await fetch(`${apiBaseUrl}/api/mood/market/${sessionId}`, {
	  headers: {
		'Content-Type': 'application/json',
		'Authorization': `Bearer ${getAuthToken()}`
	  },
	  cache: 'no-store', // Prevent caching
	});

	const elapsedTime = Date.now() - startTime;
	console.log(`📊 Market mood API response time: ${elapsedTime}ms`);

	if (!response.ok) {
	  console.error(`❌ HTTP error! status: ${response.status}`);
	  throw new Error(`HTTP error! status: ${response.status}`);
	}

	const data = await response.json();
	console.log("📊 Market mood response:", data);
	
	// Verify the data structure
	if (typeof data.averageMood === 'undefined') {
	  console.warn("⚠️ Market mood response missing averageMood field");
	}
	
	return data;
  } catch (error) {
	console.error('❌ Error fetching market mood:', error);
	showNotification(`Error fetching market mood: ${error.message}`, false);
	return null;
  }
}

function displayMarketMood(marketMood) {
  if (!marketMood) return;
  
  const marketMoodElement = document.getElementById('marketMood');
  if (marketMoodElement) {
	// Check if averageMood exists and is a number
	if (marketMood.averageMood !== undefined && !isNaN(parseFloat(marketMood.averageMood))) {
	  const moodValue = parseFloat(marketMood.averageMood);
	  marketMoodElement.textContent = `Market Mood: ${moodValue.toFixed(2)}`;
	  marketMoodElement.style.color = getMoodTextColor(moodValue);
	} else {
	  marketMoodElement.textContent = `Market Mood: N/A`;
	  marketMoodElement.style.color = getMoodTextColor(0);
	}
  }
  
  // Display transaction count if available
  const txCountElement = document.getElementById('transactionCount');
  if (txCountElement && marketMood.transactionCount) {
	txCountElement.textContent = `Based on ${marketMood.transactionCount} transactions`;
  }
}

export function triggerBurst(moodScore) {
  console.log(`🎯 triggerBurst called with score: ${moodScore}`);
  if (!p5Instance) {
	console.error("⚠️ Cannot trigger burst - p5Instance not initialized");
	return;
  }
  // If grid dimensions are not set, recalc using p5Instance's width/height.
  if (!cols || !rows) {
	console.warn("Grid dimensions missing. Recomputing grid.");
	const grid = computeGrid(p5Instance.width, p5Instance.height, 0);
	cellSize = grid.cellSize;
	cols = grid.cols;
	rows = grid.rows;
  }
  const burstColor = moodToColor(moodScore);
  const intensity = Math.min(5, Math.abs(moodScore));
  const burstCount = Math.floor(20 * (intensity / 5)) + 5;
  console.log(`🎨 Creating ${burstCount} bursts with color ${burstColor} (intensity: ${intensity})`);
  for (let i = 0; i < burstCount; i++) {
	const centerX = Math.floor(Math.random() * cols);
	const centerY = Math.floor(Math.random() * rows);
	const offset = (i / burstCount) * Math.PI * 2;
	const distance = Math.random() * 10;
	const x = Math.floor(centerX + Math.cos(offset) * distance);
	const y = Math.floor(centerY + Math.sin(offset) * distance);
	const clampedX = Math.max(0, Math.min(cols - 1, x));
	const clampedY = Math.max(0, Math.min(rows - 1, y));
	burstCells.push({
	  x: clampedX,
	  y: clampedY,
	  color: burstColor,
	  life: 120 + Math.random() * 60,
	  size: 1.5 + Math.random() * 1.5,
	  alpha: 0.85 + Math.random() * 0.15,
	  intensity: intensity
	});
  }
  console.log(`✨ Created ${burstCount} burst cells with color ${burstColor}`);
  // Restart the loop if it's not active.
  if (p5Instance && typeof p5Instance.loop === 'function' && !p5Instance.isLooping()) {
	p5Instance.loop();
  }
}

function displayMoodScores() {
	const scoresContainer = document.getElementById('moodScoresContainer');
	
	if (!scoresContainer) {
	  console.error("Error: moodScoresContainer not found in the DOM");
	  return;
	}
	
	// Get current time for the update timestamp
	const now = new Date();
	const timeStr = now.toLocaleTimeString();
	
	// Format values to display (fixing any potential NaN issues)
	const userScore = isFinite(userMoodScore) ? userMoodScore.toFixed(3) : "0.000";
	const marketScore = isFinite(marketMoodScore) ? marketMoodScore.toFixed(3) : "0.000";
	const difference = isFinite(userMoodScore) && isFinite(marketMoodScore) ? 
					   Math.abs(userMoodScore - marketMoodScore).toFixed(3) : "0.000";
	
	// Update the HTML content
	scoresContainer.innerHTML = `
	  <div style="text-align: center; margin-bottom: 8px; border-bottom: 1px solid #00FFFF; padding-bottom: 5px;">
		<span style="color: #FF00FF; font-size: 16px; font-weight: bold;">MOOD SCORES</span>
	  </div>
	  <div style="margin-bottom: 8px;">
		<span style="color: #FFA500; font-weight: bold;">User Score: </span>
		<span style="color: ${getMoodTextColor(userMoodScore)}; font-weight: bold;">${userScore}</span>
	  </div>
	  <div style="margin-bottom: 8px;">
		<span style="color: #FFA500; font-weight: bold;">Market Score: </span>
		<span style="color: ${getMoodTextColor(marketMoodScore)}; font-weight: bold;">${marketScore}</span>
	  </div>
	  <div style="margin-bottom: 8px;">
		<span style="color: #FFA500;">Difference: </span>
		<span style="font-weight: bold;">${difference}</span>
	  </div>
	  <div style="margin-bottom: 8px;">
		<span style="color: #FFA500;">Amp Factor: </span>
		<span style="font-weight: bold;">${MOOD_AMPLIFICATION_FACTOR}</span>
	  </div>
	  <div style="font-size: 12px; color: #999; text-align: right; margin-top: 10px; border-top: 1px dotted #555; padding-top: 5px;">
		Updated: ${timeStr}
	  </div>
	`;
  }

function displayMoodFactors(baseline) {
  const factorsContainer = document.getElementById('moodFactors');
  if (!factorsContainer) return;
  
  factorsContainer.innerHTML = '';
  
  const factors = [
	{ name: 'Positive Mental', value: baseline.positiveMental },
	{ name: 'Negative Mental', value: baseline.negativeMental },
	{ name: 'Positive Experience', value: baseline.positiveExperience },
	{ name: 'Negative Experience', value: baseline.negativeExperience }
  ];
  
  factors.forEach(factor => {
	const factorElement = document.createElement('div');
	factorElement.className = 'mood-factor';
	factorElement.innerHTML = `
	  <span>${factor.name}:</span>
	  <div class="factor-bar">
		<div class="factor-fill" style="width: ${Math.abs(factor.value) * 10}%; 
									   background-color: ${factor.value >= 0 ? '#4CAF50' : '#F44336'}"></div>
	  </div>
	  <span>${factor.value.toFixed(2)}</span>
	`;
	factorsContainer.appendChild(factorElement);
  });
}

function getMoodTextColor(moodScore) {
  if (moodScore >= 2) return "#FF4500"; // Red (Very Positive)
  if (moodScore >= 1) return "#FFA500"; // Orange (Positive)
  if (moodScore >= 0) return "#00FF00"; // Green (Neutral to Slightly Positive)
  if (moodScore >= -1) return "#00BFFF"; // Blue (Negative)
  return "#4B0082"; // Indigo (Very Negative)
}

function moodToColor(moodScore) {
  if (moodScore >= 2) return "rgb(255, 69, 0)"; // Red (Very Positive)
  if (moodScore >= 1) return "rgba(255, 135, 0, 0.858)"; // Orange (Positive)
  if (moodScore >= 0) return "rgb(0, 255, 0)"; // Green (Neutral to Slightly Positive)
  if (moodScore >= -1) return "rgb(0, 191, 255)"; // Blue (Negative)
  return "rgb(75, 0, 130)"; // Indigo (Very Negative)
}

// Enhanced pixelated mood art with improved sensitivity and color diversity
function pixelMoodArt(p) {
  let t = 0;
  let palette = [];
  let moodDifference = 0;
  let lastUserMood = null;
  let lastMarketMood = null;

  p.setup = function () {
	const w = Math.min(window.innerWidth, 800);
	const h = Math.min(window.innerHeight, 600);
	p.createCanvas(w, h);
	p.noSmooth(); // Ensures a pixelated look
	p.frameRate(30); // Increase framerate for smoother animations
	
	updatePalette();
  };

  function updatePalette() {
	// Only regenerate palette if mood scores have changed
	if (lastUserMood !== userMoodScore || lastMarketMood !== marketMoodScore) {
	  console.log("🔄 Regenerating mood palette with scores:", userMoodScore, marketMoodScore);
	  
	  // Dynamically adjust grid resolution based on market activity and mood difference
	  moodDifference = Math.abs(marketMoodScore - userMoodScore);
	  cellSize = Math.floor(Math.min(p.width, p.height) / (45 + moodDifference * 15)); 
	  
	  // Update the global variables so triggerBurst can access them
	  cols = Math.ceil(p.width / cellSize);
	  rows = Math.ceil(p.height / cellSize);
	  
	  palette = generateHypersensitivePalette(userMoodScore, marketMoodScore);
	  
	  // Store current values for comparison
	  lastUserMood = userMoodScore;
	  lastMarketMood = marketMoodScore;
	}
  }

  p.draw = function () {
	updatePalette();
	p.background(0);
	
	// Animation speed responds to market volatility
	const volatilityFactor = Math.abs(marketMoodScore) * 0.015;
	t += 0.015 + volatilityFactor; 

	// Draw the base mood grid
	for (let i = 0; i < cols; i++) {
	  for (let j = 0; j < rows; j++) {
		const x = i * cellSize;
		const y = j * cellSize;

		const noiseVal = p.noise(
		  i * 0.1 + Math.sin(t * 0.02) * 0.1, 
		  j * 0.1 + Math.cos(t * 0.02) * 0.1, 
		  t * 0.1
		);
		
		const moodFactor = p.map(userMoodScore, -5, 5, 0.5, 1.2) * p.map(marketMoodScore, -5, 5, 0.7, 1.1);
		const paletteIndex = Math.floor(noiseVal * palette.length * moodFactor) % palette.length;

		let col = p.color(palette[paletteIndex]);
		const moodRelationship = userMoodScore / (marketMoodScore !== 0 ? marketMoodScore : 0.01);
		const brightnessMod = p.map(moodRelationship, -5, 5, 0.5, 1.2);
		const contrastMod = p.map(marketMoodScore, -5, 5, 0.7, 1.1);
		
		let r = p.constrain(p.red(col) * brightnessMod * contrastMod, 0, 255);
		let g = p.constrain(p.green(col) * brightnessMod, 0, 255);
		let b = p.constrain(p.blue(col) * (2 - brightnessMod), 0, 255);
		let alpha = p.map(p.noise(i * 0.2, j * 0.2, t * 0.1), 0, 1, 180, 255);
		
		col = p.color(r, g, b, alpha);
		p.fill(col);
		p.noStroke();
		
		const flowSpeed = p.map(Math.abs(marketMoodScore), 0, 1, 0.1, 0.5);
		let xOffset = p.sin(t * flowSpeed + i * 0.1) * cellSize * 0.15;
		let yOffset = p.cos(t * flowSpeed + j * 0.1) * cellSize * 0.15;
		
		const pulseSize = cellSize * (1 + Math.sin(t * 0.15) * 0.1 * Math.abs(marketMoodScore));
		
		p.rect(x + xOffset, y + yOffset, pulseSize, pulseSize);
	  }
	}
	
	// Draw burst cells on top (IMPORTANT: This needs to be outside the grid loops)
	if (burstCells.length > 0) {
	  p.push(); // Save drawing state
	  
	  for (let i = burstCells.length - 1; i >= 0; i--) {
		const cell = burstCells[i];
		
		// Calculate alpha based on life
		const lifeRatio = cell.life / 120; // Normalize to 0-1
		const alpha = lifeRatio * (cell.alpha || 0.9);
		const intensity = cell.intensity || 1;
		
		// Calculate size with pulsing effect
		const pulseFactor = 1 + 0.2 * Math.sin(p.frameCount * 0.2);
		const size = cellSize * (cell.size || 1.5) * pulseFactor;
		
		// Position with slight movement
		const wobbleX = Math.sin(p.frameCount * 0.1 + i) * cellSize * 0.1 * lifeRatio;
		const wobbleY = Math.cos(p.frameCount * 0.1 + i) * cellSize * 0.1 * lifeRatio;
		const x = cell.x * cellSize + wobbleX;
		const y = cell.y * cellSize + wobbleY;
		
		// Parse the color
		let r, g, b;
		if (typeof cell.color === 'string') {
		  const match = cell.color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
		  if (match) {
			r = parseInt(match[1]);
			g = parseInt(match[2]);
			b = parseInt(match[3]);
		  } else {
			// Fallback color if parsing fails
			r = 255; g = 255; b = 255;
		  }
		} else {
		  // Another fallback
		  r = 255; g = 255; b = 255;
		}
		
		// Draw outer glow
		const glowSize = size * 1.5;
		const glowAlpha = alpha * 0.3;
		p.fill(p.color(`rgba(${r}, ${g}, ${b}, ${glowAlpha})`));
		p.rect(x - (glowSize - size)/2, y - (glowSize - size)/2, glowSize, glowSize);
		
		// Draw main burst
		p.fill(p.color(`rgba(${r}, ${g}, ${b}, ${alpha})`));
		p.rect(x, y, size, size);
		
		// Draw bright core
		if (cell.life > 60) {
		  const coreSize = size * 0.5;
		  const coreAlpha = alpha * 1.5; // Brighter than the main burst
		  p.fill(p.color(`rgba(255, 255, 255, ${coreAlpha})`));
		  p.rect(x + size/2 - coreSize/2, y + size/2 - coreSize/2, coreSize, coreSize);
		}
		
		// Update life
		cell.life -= 1 + intensity * 0.5; // Higher intensity = faster decay
		
		// Remove expired cells
		if (cell.life <= 0) {
		  burstCells.splice(i, 1);
		}
	  }
	  
	  p.pop(); // Restore drawing state
	}
  };

  p.windowResized = function () {
	const w = Math.min(window.innerWidth, 800);
	const h = Math.min(window.innerHeight, 600);
	p.resizeCanvas(w, h);
	updatePalette();
  };
}


// Generate an ultra-sensitive palette with micro-gradations for market mood visualization
function generateHypersensitivePalette(userMood, marketMood) {
  let palette = [];
  // Precise decimal calculation to avoid floating point errors
  const moodRounded = Math.round(marketMood * 100) / 100;
  // Clamp the market mood to a safe range to prevent errors
  const safeMarketMood = Math.max(-5, Math.min(5, moodRounded));

  if (safeMarketMood >= 5.0) {
	// Ultra bright golden yellow
	palette = [
	  "rgba(255, 255, 100, 0.95)", 
	  "rgba(255, 250, 50, 0.9)",  
	  "rgba(255, 245, 0, 0.85)",   
	  "rgba(255, 240, 0, 0.8)",     
	  "rgba(255, 235, 0, 0.75)",  
	  "rgba(255, 230, 50, 0.7)",    
	  "rgba(255, 225, 100, 0.65)",  
	  "rgba(255, 220, 150, 0.6)"  
	];
  } else if (safeMarketMood >= 4.9) {
	palette = [
	  "rgba(255, 253, 95, 0.95)",
	  "rgba(255, 248, 48, 0.9)",
	  "rgba(255, 243, 0, 0.85)",
	  "rgba(255, 238, 0, 0.8)",
	  "rgba(255, 233, 0, 0.75)",
	  "rgba(255, 228, 48, 0.7)",
	  "rgba(255, 223, 95, 0.65)",
	  "rgba(255, 218, 145, 0.6)"
	];
  } else if (safeMarketMood >= 4.8) {
	palette = [
	  "rgba(255, 252, 90, 0.95)",
	  "rgba(255, 247, 45, 0.9)",
	  "rgba(255, 242, 0, 0.85)",
	  "rgba(255, 237, 0, 0.8)",
	  "rgba(255, 232, 0, 0.75)",
	  "rgba(255, 227, 45, 0.7)",
	  "rgba(255, 222, 90, 0.65)",
	  "rgba(255, 217, 140, 0.6)"
	];
  } else if (safeMarketMood >= 4.7) {
	palette = [
	  "rgba(255, 251, 85, 0.95)",
	  "rgba(255, 246, 43, 0.9)",
	  "rgba(255, 241, 0, 0.85)",
	  "rgba(255, 236, 0, 0.8)",
	  "rgba(255, 231, 0, 0.75)",
	  "rgba(255, 226, 43, 0.7)",
	  "rgba(255, 221, 85, 0.65)",
	  "rgba(255, 216, 135, 0.6)"
	];
  } else if (safeMarketMood >= 4.6) {
	palette = [
	  "rgba(255, 250, 83, 0.95)",
	  "rgba(255, 245, 42, 0.9)",
	  "rgba(255, 240, 0, 0.85)",
	  "rgba(255, 235, 0, 0.8)",
	  "rgba(255, 230, 0, 0.75)",
	  "rgba(255, 225, 42, 0.7)",
	  "rgba(255, 220, 83, 0.65)",
	  "rgba(255, 215, 130, 0.6)"
	];
  } else if (safeMarketMood >= 4.5) {
	palette = [
	  "rgba(255, 250, 80, 0.95)",
	  "rgba(255, 245, 40, 0.9)",
	  "rgba(255, 240, 0, 0.85)",
	  "rgba(255, 235, 0, 0.8)",
	  "rgba(255, 230, 0, 0.75)",
	  "rgba(255, 225, 40, 0.7)",
	  "rgba(255, 220, 80, 0.65)",
	  "rgba(255, 215, 120, 0.6)"
	];
  } else if (safeMarketMood >= 4.4) {
	palette = [
	  "rgba(255, 248, 75, 0.95)",
	  "rgba(255, 243, 38, 0.9)",
	  "rgba(255, 238, 0, 0.85)",
	  "rgba(255, 233, 0, 0.8)",
	  "rgba(255, 228, 0, 0.75)",
	  "rgba(255, 223, 38, 0.7)",
	  "rgba(255, 218, 75, 0.65)",
	  "rgba(255, 213, 115, 0.6)"
	];
  } else if (safeMarketMood >= 4.3) {
	palette = [
	  "rgba(255, 247, 70, 0.95)",
	  "rgba(255, 242, 35, 0.9)",
	  "rgba(255, 237, 0, 0.85)",
	  "rgba(255, 232, 0, 0.8)",
	  "rgba(255, 227, 0, 0.75)",
	  "rgba(255, 222, 35, 0.7)",
	  "rgba(255, 217, 70, 0.65)",
	  "rgba(255, 212, 110, 0.6)"
	];
  } else if (safeMarketMood >= 4.2) {
	palette = [
	  "rgba(255, 246, 65, 0.95)",
	  "rgba(255, 241, 33, 0.9)",
	  "rgba(255, 236, 0, 0.85)",
	  "rgba(255, 231, 0, 0.8)",
	  "rgba(255, 226, 0, 0.75)",
	  "rgba(255, 221, 33, 0.7)",
	  "rgba(255, 216, 65, 0.65)",
	  "rgba(255, 211, 105, 0.6)"
	];
  } else if (safeMarketMood >= 4.1) {
	palette = [
	  "rgba(255, 245, 63, 0.95)",
	  "rgba(255, 240, 32, 0.9)",
	  "rgba(255, 235, 0, 0.85)",
	  "rgba(255, 230, 0, 0.8)",
	  "rgba(255, 225, 0, 0.75)",
	  "rgba(255, 220, 32, 0.7)",
	  "rgba(255, 215, 63, 0.65)",
	  "rgba(255, 210, 100, 0.6)"
	];
  }  else if (safeMarketMood >= 4.0) {
	palette = [
	  "rgba(255, 245, 60, 0.95)",
	  "rgba(255, 240, 30, 0.9)",
	  "rgba(255, 235, 0, 0.85)",
	  "rgba(255, 230, 0, 0.8)",
	  "rgba(255, 225, 0, 0.75)",
	  "rgba(255, 220, 30, 0.7)",
	  "rgba(255, 215, 60, 0.65)",
	  "rgba(255, 210, 90, 0.6)"
	];
  } else if (safeMarketMood >= 3.9) {
	palette = [
	  "rgba(255, 244, 55, 0.95)",
	  "rgba(255, 239, 28, 0.9)",
	  "rgba(255, 234, 0, 0.85)",
	  "rgba(255, 229, 0, 0.8)",
	  "rgba(255, 224, 0, 0.75)",
	  "rgba(255, 219, 28, 0.7)",
	  "rgba(255, 214, 55, 0.65)",
	  "rgba(255, 209, 85, 0.6)"
	];
  } else if (safeMarketMood >= 3.8) {
	palette = [
	  "rgba(255, 243, 53, 0.95)",
	  "rgba(255, 238, 26, 0.9)",
	  "rgba(255, 233, 0, 0.85)",
	  "rgba(255, 228, 0, 0.8)",
	  "rgba(255, 223, 0, 0.75)",
	  "rgba(255, 218, 26, 0.7)",
	  "rgba(255, 213, 53, 0.65)",
	  "rgba(255, 208, 83, 0.6)"
	];
  } else if (safeMarketMood >= 3.7) {
	palette = [
	  "rgba(255, 242, 50, 0.95)",
	  "rgba(255, 237, 25, 0.9)",
	  "rgba(255, 232, 0, 0.85)",
	  "rgba(255, 227, 0, 0.8)",
	  "rgba(255, 222, 0, 0.75)",
	  "rgba(255, 217, 25, 0.7)",
	  "rgba(255, 212, 50, 0.65)",
	  "rgba(255, 207, 80, 0.6)"
	];
  } else if (safeMarketMood >= 3.6) {
	palette = [
	  "rgba(255, 241, 48, 0.95)",
	  "rgba(255, 236, 24, 0.9)",
	  "rgba(255, 231, 0, 0.85)",
	  "rgba(255, 226, 0, 0.8)",
	  "rgba(255, 221, 0, 0.75)",
	  "rgba(255, 216, 24, 0.7)",
	  "rgba(255, 211, 48, 0.65)",
	  "rgba(255, 206, 75, 0.6)"
	];
  } else if (safeMarketMood >= 3.5) {
	palette = [
	  "rgba(255, 240, 45, 0.95)",
	  "rgba(255, 235, 22, 0.9)",
	  "rgba(255, 230, 0, 0.85)",
	  "rgba(255, 225, 0, 0.8)",
	  "rgba(255, 220, 0, 0.75)",
	  "rgba(255, 215, 22, 0.7)",
	  "rgba(255, 210, 45, 0.65)",
	  "rgba(255, 205, 70, 0.6)"
	];
  } else if (safeMarketMood >= 3.4) {
	palette = [
	  "rgba(255, 239, 40, 0.95)",
	  "rgba(255, 234, 20, 0.9)",
	  "rgba(255, 229, 0, 0.85)",
	  "rgba(255, 224, 0, 0.8)",
	  "rgba(255, 219, 0, 0.75)",
	  "rgba(255, 214, 20, 0.7)",
	  "rgba(255, 209, 40, 0.65)",
	  "rgba(255, 204, 65, 0.6)"
	];
  } else if (safeMarketMood >= 3.3) {
	palette = [
	  "rgba(255, 238, 38, 0.95)",
	  "rgba(255, 233, 18, 0.9)",
	  "rgba(255, 228, 0, 0.85)",
	  "rgba(255, 223, 0, 0.8)",
	  "rgba(255, 218, 0, 0.75)",
	  "rgba(255, 213, 18, 0.7)",
	  "rgba(255, 208, 38, 0.65)",
	  "rgba(255, 203, 60, 0.6)"
	];
  } else if (safeMarketMood >= 3.2) {
	palette = [
	  "rgba(255, 237, 35, 0.95)",
	  "rgba(255, 232, 16, 0.9)",
	  "rgba(255, 227, 0, 0.85)",
	  "rgba(255, 222, 0, 0.8)",
	  "rgba(255, 217, 0, 0.75)",
	  "rgba(255, 212, 16, 0.7)",
	  "rgba(255, 207, 35, 0.65)",
	  "rgba(255, 202, 55, 0.6)"
	];
  } else if (safeMarketMood >= 3.1) {
	palette = [
	  "rgba(255, 236, 30, 0.95)",
	  "rgba(255, 231, 14, 0.9)",
	  "rgba(255, 226, 0, 0.85)",
	  "rgba(255, 221, 0, 0.8)",
	  "rgba(255, 216, 0, 0.75)",
	  "rgba(255, 211, 14, 0.7)",
	  "rgba(255, 206, 30, 0.65)",
	  "rgba(255, 201, 50, 0.6)"
	];
  } else if (safeMarketMood >= 3.0) {
	palette = [
	  "rgba(255, 235, 25, 0.95)",
	  "rgba(255, 230, 12, 0.9)",
	  "rgba(255, 225, 0, 0.85)",
	  "rgba(255, 220, 0, 0.8)",
	  "rgba(255, 215, 0, 0.75)",
	  "rgba(255, 210, 12, 0.7)",
	  "rgba(255, 205, 25, 0.65)",
	  "rgba(255, 200, 45, 0.6)"
	];
  } else if (safeMarketMood >= 2.9) {
	palette = [
	  "rgba(255, 234, 23, 0.95)",
	  "rgba(255, 229, 11, 0.9)",
	  "rgba(255, 224, 0, 0.85)",
	  "rgba(255, 219, 0, 0.8)",
	  "rgba(255, 214, 0, 0.75)",
	  "rgba(255, 209, 11, 0.7)",
	  "rgba(255, 204, 23, 0.65)",
	  "rgba(255, 199, 43, 0.6)"
	];
  } else if (safeMarketMood >= 2.8) {
	palette = [
	  "rgba(255, 232, 20, 0.95)",
	  "rgba(255, 227, 10, 0.9)",
	  "rgba(255, 222, 0, 0.85)",
	  "rgba(255, 217, 0, 0.8)",
	  "rgba(255, 212, 0, 0.75)",
	  "rgba(255, 207, 10, 0.7)",
	  "rgba(255, 202, 20, 0.65)",
	  "rgba(255, 197, 40, 0.6)"
	];
  } else if (safeMarketMood >= 2.7) {
	palette = [
	  "rgba(255, 230, 18, 0.95)",
	  "rgba(255, 225, 8, 0.9)",
	  "rgba(255, 220, 0, 0.85)",
	  "rgba(255, 215, 0, 0.8)",
	  "rgba(255, 210, 0, 0.75)",
	  "rgba(255, 205, 8, 0.7)",
	  "rgba(255, 200, 18, 0.65)",
	  "rgba(255, 195, 38, 0.6)"
	];
  } else if (safeMarketMood >= 2.6) {
	palette = [
	  "rgba(255, 228, 15, 0.95)",
	  "rgba(255, 223, 7, 0.9)",
	  "rgba(255, 218, 0, 0.85)",
	  "rgba(255, 213, 0, 0.8)",
	  "rgba(255, 208, 0, 0.75)",
	  "rgba(255, 203, 7, 0.7)",
	  "rgba(255, 198, 15, 0.65)",
	  "rgba(255, 193, 35, 0.6)"
	];
  } else if (safeMarketMood >= 2.5) {
	palette = [
	  "rgba(255, 225, 12, 0.95)",
	  "rgba(255, 220, 6, 0.9)",
	  "rgba(255, 215, 0, 0.85)",
	  "rgba(255, 210, 0, 0.8)",
	  "rgba(255, 205, 0, 0.75)",
	  "rgba(255, 200, 6, 0.7)",
	  "rgba(255, 195, 12, 0.65)",
	  "rgba(255, 190, 32, 0.6)"
	];
  } else if (safeMarketMood >= 2.4) {
	palette = [
	  "rgba(255, 223, 10, 0.95)",
	  "rgba(255, 218, 5, 0.9)",
	  "rgba(255, 213, 0, 0.85)",
	  "rgba(255, 208, 0, 0.8)",
	  "rgba(255, 203, 0, 0.75)",
	  "rgba(255, 198, 5, 0.7)",
	  "rgba(255, 193, 10, 0.65)",
	  "rgba(255, 188, 30, 0.6)"
	];
  } else if (safeMarketMood >= 2.3) {
	palette = [
	  "rgba(255, 222, 8, 0.95)",
	  "rgba(255, 217, 4, 0.9)",
	  "rgba(255, 212, 0, 0.85)",
	  "rgba(255, 207, 0, 0.8)",
	  "rgba(255, 202, 0, 0.75)",
	  "rgba(255, 197, 4, 0.7)",
	  "rgba(255, 192, 8, 0.65)",
	  "rgba(255, 187, 28, 0.6)"
	];
  } else if (safeMarketMood >= 2.2) {
	palette = [
	  "rgba(255, 221, 6, 0.95)",
	  "rgba(255, 216, 3, 0.9)",
	  "rgba(255, 211, 0, 0.85)",
	  "rgba(255, 206, 0, 0.8)",
	  "rgba(255, 201, 0, 0.75)",
	  "rgba(255, 196, 3, 0.7)",
	  "rgba(255, 191, 6, 0.65)",
	  "rgba(255, 186, 26, 0.6)"
	];
  } else if (safeMarketMood >= 2.1) {
	palette = [
	  "rgba(255, 220, 4, 0.95)",
	  "rgba(255, 215, 2, 0.9)",
	  "rgba(255, 210, 0, 0.85)",
	  "rgba(255, 205, 0, 0.8)",
	  "rgba(255, 200, 0, 0.75)",
	  "rgba(255, 195, 2, 0.7)",
	  "rgba(255, 190, 4, 0.65)",
	  "rgba(255, 185, 24, 0.6)"
	];
  } else if (safeMarketMood >= 2.0) {
	palette = [
	  "rgba(255, 255, 30, 0.9)",  
	  "rgba(255, 240, 0, 0.85)",   
	  "rgba(255, 220, 0, 0.8)",   
	  "rgba(255, 215, 0, 0.78)",   
	  "rgba(255, 235, 60, 0.75)",  
	  "rgba(255, 248, 130, 0.7)",  
	  "rgba(255, 253, 190, 0.65)",
	  "rgba(255, 255, 220, 0.6)"   
	];
  } else if (safeMarketMood >= 1.95) {
	palette = [
	  "rgba(255, 253, 25, 0.9)",
	  "rgba(255, 235, 0, 0.85)",
	  "rgba(255, 215, 0, 0.8)",
	  "rgba(255, 210, 0, 0.78)",
	  "rgba(255, 230, 55, 0.75)",
	  "rgba(255, 245, 125, 0.7)",
	  "rgba(255, 250, 185, 0.65)",
	  "rgba(255, 253, 215, 0.6)"
	];
  } else if (safeMarketMood >= 1.9) {
	palette = [
	  "rgba(255, 250, 20, 0.9)",
	  "rgba(255, 230, 0, 0.85)",
	  "rgba(255, 210, 0, 0.8)",
	  "rgba(255, 205, 0, 0.78)",
	  "rgba(255, 225, 50, 0.75)",
	  "rgba(255, 240, 120, 0.7)",
	  "rgba(255, 245, 180, 0.65)",
	  "rgba(255, 250, 210, 0.6)"
	];
  } else if (safeMarketMood >= 1.85) {
	palette = [
	  "rgba(255, 245, 15, 0.9)",
	  "rgba(255, 225, 0, 0.85)",
	  "rgba(255, 205, 0, 0.8)",
	  "rgba(255, 200, 0, 0.78)",
	  "rgba(255, 220, 45, 0.75)",
	  "rgba(255, 235, 115, 0.7)",
	  "rgba(255, 240, 175, 0.65)",
	  "rgba(255, 245, 205, 0.6)"
	];
  } else if (safeMarketMood >= 1.8) {
	palette = [
	  "rgba(255, 240, 10, 0.9)",
	  "rgba(255, 220, 0, 0.85)",
	  "rgba(255, 200, 0, 0.8)",
	  "rgba(255, 195, 0, 0.78)",
	  "rgba(255, 215, 40, 0.75)",
	  "rgba(255, 230, 110, 0.7)",
	  "rgba(255, 235, 170, 0.65)",
	  "rgba(255, 240, 200, 0.6)"
	];
  } else if (safeMarketMood >= 1.75) {
	palette = [
	  "rgba(255, 235, 5, 0.9)",
	  "rgba(255, 215, 0, 0.85)",
	  "rgba(255, 195, 0, 0.8)",
	  "rgba(255, 190, 0, 0.78)",
	  "rgba(255, 210, 35, 0.75)",
	  "rgba(255, 225, 105, 0.7)",
	  "rgba(255, 230, 165, 0.65)",
	  "rgba(255, 235, 195, 0.6)"
	];
  } else if (safeMarketMood >= 1.7) {
	// Transitions to more orange-gold tones
	palette = [
	  "rgba(255, 230, 0, 0.9)",
	  "rgba(255, 210, 0, 0.85)",
	  "rgba(255, 190, 0, 0.8)",
	  "rgba(255, 185, 0, 0.78)",
	  "rgba(255, 205, 30, 0.75)",
	  "rgba(255, 220, 100, 0.7)",
	  "rgba(255, 225, 160, 0.65)",
	  "rgba(255, 230, 190, 0.6)"
	];
  } else if (safeMarketMood >= 1.65) {
	palette = [
	  "rgba(255, 225, 0, 0.9)",
	  "rgba(255, 205, 0, 0.85)",
	  "rgba(255, 185, 0, 0.8)",
	  "rgba(255, 180, 0, 0.78)",
	  "rgba(255, 200, 25, 0.75)",
	  "rgba(255, 215, 95, 0.7)",
	  "rgba(255, 220, 155, 0.65)",
	  "rgba(255, 225, 185, 0.6)"
	];
  } else if (safeMarketMood >= 1.6) {
	palette = [
	  "rgba(255, 220, 0, 0.9)",
	  "rgba(255, 200, 0, 0.85)",
	  "rgba(255, 180, 0, 0.8)",
	  "rgba(255, 175, 0, 0.78)",
	  "rgba(255, 195, 20, 0.75)",
	  "rgba(255, 210, 90, 0.7)",
	  "rgba(255, 215, 150, 0.65)",
	  "rgba(255, 220, 180, 0.6)"
	];
  } else if (safeMarketMood >= 1.55) {
	palette = [
	  "rgba(255, 215, 0, 0.9)",
	  "rgba(255, 195, 0, 0.85)",
	  "rgba(255, 175, 0, 0.8)",
	  "rgba(255, 170, 0, 0.78)",
	  "rgba(255, 190, 15, 0.75)",
	  "rgba(255, 205, 85, 0.7)",
	  "rgba(255, 210, 145, 0.65)",
	  "rgba(255, 215, 175, 0.6)"
	];
  } else if (safeMarketMood >= 1.5) {
	palette = [
	  "rgba(255, 210, 0, 0.9)",
	  "rgba(255, 190, 0, 0.85)",
	  "rgba(255, 170, 0, 0.8)",
	  "rgba(255, 165, 0, 0.78)",
	  "rgba(255, 185, 10, 0.75)",
	  "rgba(255, 200, 80, 0.7)",
	  "rgba(255, 205, 140, 0.65)",
	  "rgba(255, 210, 170, 0.6)"
	];
  } else if (safeMarketMood >= 1.45) {
	palette = [
	  "rgba(255, 205, 0, 0.9)",
	  "rgba(255, 185, 0, 0.85)",
	  "rgba(255, 165, 0, 0.8)",
	  "rgba(255, 160, 0, 0.78)",
	  "rgba(255, 180, 5, 0.75)",
	  "rgba(255, 195, 75, 0.7)",
	  "rgba(255, 200, 135, 0.65)",
	  "rgba(255, 205, 165, 0.6)"
	];
  } else if (safeMarketMood >= 1.4) {
	palette = [
	  "rgba(255, 200, 0, 0.9)",
	  "rgba(255, 180, 0, 0.85)",
	  "rgba(255, 160, 0, 0.8)",
	  "rgba(255, 155, 0, 0.78)",
	  "rgba(255, 175, 0, 0.75)",
	  "rgba(255, 190, 70, 0.7)",
	  "rgba(255, 195, 130, 0.65)",
	  "rgba(255, 200, 160, 0.6)"
	];
  } else if (safeMarketMood >= 1.35) {
	palette = [
	  "rgba(255, 195, 0, 0.9)",
	  "rgba(255, 175, 0, 0.85)",
	  "rgba(255, 155, 0, 0.8)",
	  "rgba(255, 150, 0, 0.78)",
	  "rgba(255, 170, 0, 0.75)",
	  "rgba(255, 185, 65, 0.7)",
	  "rgba(255, 190, 125, 0.65)",
	  "rgba(255, 195, 155, 0.6)"
	];
  } else if (safeMarketMood >= 1.3) {
	palette = [
	  "rgba(255, 190, 0, 0.9)",
	  "rgba(255, 170, 0, 0.85)",
	  "rgba(255, 150, 0, 0.8)",
	  "rgba(255, 145, 0, 0.78)",
	  "rgba(255, 165, 0, 0.75)",
	  "rgba(255, 180, 60, 0.7)",
	  "rgba(255, 185, 120, 0.65)",
	  "rgba(255, 190, 150, 0.6)"
	];
  } else if (safeMarketMood >= 1.25) {
	palette = [
	  "rgba(255, 185, 0, 0.9)",
	  "rgba(255, 165, 0, 0.85)",
	  "rgba(255, 145, 0, 0.8)",
	  "rgba(255, 140, 0, 0.78)",
	  "rgba(255, 160, 0, 0.75)",
	  "rgba(255, 175, 55, 0.7)",
	  "rgba(255, 180, 115, 0.65)",
	  "rgba(255, 185, 145, 0.6)"
	];
  } else if (safeMarketMood >= 1.2) {
	palette = [
	  "rgba(255, 180, 0, 0.9)",
	  "rgba(255, 160, 0, 0.85)",
	  "rgba(255, 140, 0, 0.8)",
	  "rgba(255, 135, 0, 0.78)",
	  "rgba(255, 155, 0, 0.75)",
	  "rgba(255, 170, 50, 0.7)",
	  "rgba(255, 175, 110, 0.65)",
	  "rgba(255, 180, 140, 0.6)"
	];
  } else if (safeMarketMood >= 1.15) {
	palette = [
	  "rgba(255, 175, 0, 0.9)",
	  "rgba(255, 155, 0, 0.85)",
	  "rgba(255, 135, 0, 0.8)",
	  "rgba(255, 130, 0, 0.78)",
	  "rgba(255, 150, 0, 0.75)",
	  "rgba(255, 165, 45, 0.7)",
	  "rgba(255, 170, 105, 0.65)",
	  "rgba(255, 175, 135, 0.6)"
	];
  } else if (safeMarketMood >= 1.1) {
	palette = [
	  "rgba(255, 170, 0, 0.9)",
	  "rgba(255, 150, 0, 0.85)",
	  "rgba(255, 130, 0, 0.8)",
	  "rgba(255, 125, 0, 0.78)",
	  "rgba(255, 145, 0, 0.75)",
	  "rgba(255, 160, 40, 0.7)",
	  "rgba(255, 165, 100, 0.65)",
	  "rgba(255, 170, 130, 0.6)"
	];
  } else if (safeMarketMood >= 1.05) {
	palette = [
	  "rgba(255, 165, 0, 0.9)",
	  "rgba(255, 145, 0, 0.85)",
	  "rgba(255, 125, 0, 0.8)",
	  "rgba(255, 120, 0, 0.78)",
	  "rgba(255, 140, 0, 0.75)",
	  "rgba(255, 155, 35, 0.7)",
	  "rgba(255, 160, 95, 0.65)",
	  "rgba(255, 165, 125, 0.6)"
	];
  } else if (safeMarketMood >= 1.0) {
	palette = [
	  "rgba(255, 160, 0, 0.9)",
	  "rgba(255, 140, 0, 0.85)",
	  "rgba(255, 120, 0, 0.8)",
	  "rgba(255, 115, 0, 0.78)",
	  "rgba(255, 135, 0, 0.75)",
	  "rgba(255, 150, 30, 0.7)",
	  "rgba(255, 155, 90, 0.65)",
	  "rgba(255, 160, 120, 0.6)"
	];
  } else if (safeMarketMood >= 0.95) {
	// Transition to softer oranges with blue accents beginning
	palette = [
	  "rgba(255, 155, 0, 0.9)",
	  "rgba(255, 135, 0, 0.85)",
	  "rgba(255, 115, 0, 0.8)",
	  "rgba(255, 110, 0, 0.78)",
	  "rgba(255, 130, 0, 0.75)",
	  "rgba(255, 145, 25, 0.7)",
	  "rgba(245, 150, 85, 0.65)",
	  "rgba(235, 155, 120, 0.6)"
	];
  } else if (safeMarketMood >= 0.9) {
	palette = [
	  "rgba(255, 150, 0, 0.9)",
	  "rgba(255, 130, 0, 0.85)",
	  "rgba(255, 110, 0, 0.8)",
	  "rgba(255, 105, 0, 0.78)",
	  "rgba(255, 125, 0, 0.75)",
	  "rgba(245, 140, 20, 0.7)",
	  "rgba(235, 145, 80, 0.65)",
	  "rgba(225, 150, 120, 0.6)"
	];
  } else if (safeMarketMood >= 0.85) {
	palette = [
	  "rgba(255, 145, 0, 0.9)",
	  "rgba(255, 125, 0, 0.85)",
	  "rgba(250, 105, 0, 0.8)",
	  "rgba(240, 100, 0, 0.78)",
	  "rgba(235, 120, 0, 0.75)",
	  "rgba(225, 135, 20, 0.7)",
	  "rgba(215, 140, 80, 0.65)",
	  "rgba(205, 145, 130, 0.6)"
	];
  } else if (safeMarketMood >= 0.8) {
	palette = [
	  "rgba(250, 140, 0, 0.9)",
	  "rgba(245, 120, 0, 0.85)",
	  "rgba(240, 100, 0, 0.8)",
	  "rgba(230, 95, 10, 0.78)",
	  "rgba(220, 115, 20, 0.75)",
	  "rgba(210, 130, 40, 0.7)",
	  "rgba(200, 135, 90, 0.65)",
	  "rgba(190, 140, 140, 0.6)"
	];
  } else if (safeMarketMood >= 0.75) {
	palette = [
	  "rgba(245, 135, 0, 0.9)",
	  "rgba(235, 115, 0, 0.85)",
	  "rgba(225, 95, 10, 0.8)",
	  "rgba(215, 90, 20, 0.78)",
	  "rgba(205, 110, 30, 0.75)",
	  "rgba(195, 125, 50, 0.7)",
	  "rgba(185, 130, 100, 0.65)",
	  "rgba(175, 135, 150, 0.6)"
	];
  } else if (safeMarketMood >= 0.7) {
	palette = [
	  "rgba(240, 130, 10, 0.9)",
	  "rgba(230, 110, 15, 0.85)",
	  "rgba(220, 90, 20, 0.8)",
	  "rgba(210, 85, 30, 0.78)",
	  "rgba(200, 105, 40, 0.75)",
	  "rgba(190, 120, 60, 0.7)",
	  "rgba(180, 125, 110, 0.65)",
	  "rgba(170, 130, 160, 0.6)"
	];
  } else if (safeMarketMood >= 0.65) {
	palette = [
	  "rgba(235, 125, 20, 0.9)",
	  "rgba(225, 105, 25, 0.85)",
	  "rgba(215, 85, 30, 0.8)",
	  "rgba(205, 80, 40, 0.78)",
	  "rgba(195, 100, 50, 0.75)",
	  "rgba(185, 115, 70, 0.7)",
	  "rgba(175, 120, 120, 0.65)",
	  "rgba(165, 125, 170, 0.6)"
	];
  } else if (safeMarketMood >= 0.6) {
	palette = [
	  "rgba(230, 120, 30, 0.9)",
	  "rgba(220, 100, 35, 0.85)",
	  "rgba(210, 80, 40, 0.8)",
	  "rgba(200, 75, 50, 0.78)",
	  "rgba(190, 95, 60, 0.75)",
	  "rgba(180, 110, 80, 0.7)",
	  "rgba(170, 115, 130, 0.65)",
	  "rgba(160, 120, 180, 0.6)"
	];
  } else if (safeMarketMood >= 0.55) {
	palette = [
	  "rgba(225, 115, 40, 0.9)",
	  "rgba(215, 95, 45, 0.85)",
	  "rgba(205, 75, 50, 0.8)",
	  "rgba(195, 70, 60, 0.78)",
	  "rgba(185, 90, 70, 0.75)",
	  "rgba(175, 105, 90, 0.7)",
	  "rgba(165, 110, 140, 0.65)",
	  "rgba(155, 115, 190, 0.6)"
	];
  } else if (safeMarketMood >= 0.5) {
	palette = [
	  "rgba(220, 110, 50, 0.9)",
	  "rgba(210, 90, 55, 0.85)",
	  "rgba(200, 70, 60, 0.8)",
	  "rgba(190, 65, 70, 0.78)",
	  "rgba(180, 85, 80, 0.75)",
	  "rgba(170, 100, 100, 0.7)",
	  "rgba(160, 105, 150, 0.65)",
	  "rgba(150, 110, 200, 0.6)"
	];
  } else if (safeMarketMood >= 0.45) {
	// Orange fading, blue intensifying
	palette = [
	  "rgba(215, 105, 60, 0.9)",
	  "rgba(205, 85, 65, 0.85)",
	  "rgba(195, 65, 70, 0.8)",
	  "rgba(185, 60, 80, 0.78)",
	  "rgba(175, 80, 90, 0.75)",
	  "rgba(165, 95, 110, 0.7)",
	  "rgba(155, 100, 160, 0.65)",
	  "rgba(145, 105, 210, 0.6)"
	];
  } else if (safeMarketMood >= 0.4) {
	palette = [
	  "rgba(210, 100, 70, 0.9)",
	  "rgba(200, 80, 75, 0.85)",
	  "rgba(190, 60, 80, 0.8)",
	  "rgba(180, 55, 90, 0.78)",
	  "rgba(170, 75, 100, 0.75)",
	  "rgba(160, 90, 120, 0.7)",
	  "rgba(150, 95, 170, 0.65)",
	  "rgba(140, 100, 220, 0.6)"
	];
  } else if (safeMarketMood >= 0.35) {
	palette = [
	  "rgba(205, 95, 80, 0.9)",
	  "rgba(195, 75, 85, 0.85)",
	  "rgba(185, 55, 90, 0.8)",
	  "rgba(175, 50, 100, 0.78)",
	  "rgba(165, 70, 110, 0.75)",
	  "rgba(155, 85, 130, 0.7)",
	  "rgba(145, 90, 180, 0.65)",
	  "rgba(135, 95, 230, 0.6)"
	];
  } else if (safeMarketMood >= 0.3) {
	palette = [
	  "rgba(200, 90, 90, 0.9)",
	  "rgba(190, 70, 95, 0.85)",
	  "rgba(180, 50, 100, 0.8)",
	  "rgba(170, 45, 110, 0.78)",
	  "rgba(160, 65, 120, 0.75)",
	  "rgba(150, 80, 140, 0.7)",
	  "rgba(140, 85, 190, 0.65)",
	  "rgba(130, 90, 235, 0.6)"
	];
  } else if (safeMarketMood >= 0.25) {
	palette = [
	  "rgba(195, 85, 100, 0.9)",
	  "rgba(185, 65, 105, 0.85)",
	  "rgba(175, 45, 110, 0.8)",
	  "rgba(165, 40, 120, 0.78)",
	  "rgba(155, 60, 130, 0.75)",
	  "rgba(145, 75, 150, 0.7)",
	  "rgba(135, 80, 200, 0.65)",
	  "rgba(125, 85, 240, 0.6)"
	];
  } else if (safeMarketMood >= 0.2) {
	palette = [
	  "rgba(190, 80, 110, 0.9)",
	  "rgba(180, 60, 115, 0.85)",
	  "rgba(170, 40, 120, 0.8)",
	  "rgba(160, 35, 130, 0.78)",
	  "rgba(150, 55, 140, 0.75)",
	  "rgba(140, 70, 160, 0.7)",
	  "rgba(130, 75, 210, 0.65)",
	  "rgba(120, 80, 245, 0.6)"
	];
  } else if (safeMarketMood >= 0.15) {
	palette = [
	  "rgba(185, 75, 120, 0.9)",
	  "rgba(175, 55, 125, 0.85)",
	  "rgba(165, 35, 130, 0.8)",
	  "rgba(155, 30, 140, 0.78)",
	  "rgba(145, 50, 150, 0.75)",
	  "rgba(135, 65, 170, 0.7)",
	  "rgba(125, 70, 220, 0.65)",
	  "rgba(115, 75, 250, 0.6)"
	];
  } else if (safeMarketMood >= 0.1) {
	palette = [
	  "rgba(180, 70, 130, 0.9)",
	  "rgba(170, 50, 135, 0.85)",
	  "rgba(160, 30, 140, 0.8)",
	  "rgba(150, 25, 150, 0.78)",
	  "rgba(140, 45, 160, 0.75)",
	  "rgba(130, 60, 180, 0.7)",
	  "rgba(120, 65, 225, 0.65)",
	  "rgba(110, 70, 255, 0.6)"
	];
  } else if (safeMarketMood >= 0.05) {
	palette = [
	  "rgba(175, 65, 140, 0.9)",
	  "rgba(165, 45, 145, 0.85)",
	  "rgba(155, 25, 150, 0.8)",
	  "rgba(145, 20, 160, 0.78)",
	  "rgba(135, 40, 170, 0.75)",
	  "rgba(125, 55, 190, 0.7)",
	  "rgba(115, 60, 230, 0.65)",
	  "rgba(105, 65, 255, 0.6)"
	];
  } else if (safeMarketMood >= 0.0) {
	palette = [
	  "rgba(170, 60, 150, 0.9)",
	  "rgba(160, 40, 155, 0.85)",
	  "rgba(150, 20, 160, 0.8)",
	  "rgba(140, 15, 170, 0.78)",
	  "rgba(130, 35, 180, 0.75)",
	  "rgba(120, 50, 200, 0.7)",
	  "rgba(110, 55, 235, 0.65)",
	  "rgba(100, 60, 255, 0.6)"
	];
  } else if (safeMarketMood >= -0.05) {
	palette = [
	  "rgba(160, 50, 165, 0.9)",
	  "rgba(150, 35, 170, 0.85)",
	  "rgba(140, 15, 175, 0.8)",
	  "rgba(130, 10, 180, 0.78)",
	  "rgba(120, 30, 190, 0.75)",
	  "rgba(110, 45, 210, 0.7)",
	  "rgba(100, 50, 240, 0.65)",
	  "rgba(90, 55, 255, 0.6)"
	];
  } else if (safeMarketMood >= -0.1) {
	palette = [
	  "rgba(150, 45, 175, 0.9)",
	  "rgba(140, 30, 180, 0.85)",
	  "rgba(130, 10, 185, 0.8)",
	  "rgba(120, 5, 190, 0.78)",
	  "rgba(110, 25, 200, 0.75)",
	  "rgba(100, 40, 220, 0.7)",
	  "rgba(90, 45, 245, 0.65)",
	  "rgba(80, 50, 255, 0.6)"
	];
  } else if (safeMarketMood >= -0.15) {
	palette = [
	  "rgba(140, 40, 185, 0.9)",
	  "rgba(130, 25, 190, 0.85)",
	  "rgba(120, 5, 195, 0.8)",
	  "rgba(110, 0, 200, 0.78)",
	  "rgba(100, 20, 210, 0.75)",
	  "rgba(90, 35, 225, 0.7)",
	  "rgba(80, 40, 245, 0.65)",
	  "rgba(70, 45, 255, 0.6)"
	];
  } else if (safeMarketMood >= -0.2) {
	palette = [
	  "rgba(130, 35, 195, 0.9)",
	  "rgba(120, 20, 200, 0.85)",
	  "rgba(110, 0, 205, 0.8)",
	  "rgba(100, 0, 210, 0.78)",
	  "rgba(90, 15, 215, 0.75)",
	  "rgba(80, 30, 230, 0.7)",
	  "rgba(70, 35, 245, 0.65)",
	  "rgba(60, 40, 255, 0.6)"
	];
  } else if (safeMarketMood >= -0.25) {
	palette = [
	  "rgba(120, 30, 205, 0.9)",
	  "rgba(110, 15, 210, 0.85)",
	  "rgba(100, 0, 215, 0.8)",
	  "rgba(90, 0, 220, 0.78)",
	  "rgba(80, 10, 225, 0.75)",
	  "rgba(70, 25, 235, 0.7)",
	  "rgba(60, 30, 245, 0.65)",
	  "rgba(50, 35, 255, 0.6)"
	];
  } else if (safeMarketMood >= -0.3) {
	palette = [
	  "rgba(110, 25, 215, 0.9)",
	  "rgba(100, 10, 220, 0.85)",
	  "rgba(90, 0, 225, 0.8)",
	  "rgba(80, 0, 230, 0.78)",
	  "rgba(70, 5, 235, 0.75)",
	  "rgba(60, 20, 240, 0.7)",
	  "rgba(50, 25, 245, 0.65)",
	  "rgba(40, 30, 255, 0.6)"
	];
  } else if (safeMarketMood >= -0.35) {
	palette = [
	  "rgba(100, 20, 225, 0.9)",
	  "rgba(90, 5, 230, 0.85)",
	  "rgba(80, 0, 235, 0.8)",
	  "rgba(70, 0, 240, 0.78)",
	  "rgba(60, 0, 245, 0.75)",
	  "rgba(50, 15, 245, 0.7)",
	  "rgba(40, 20, 250, 0.65)",
	  "rgba(30, 25, 255, 0.6)"
	];
  } else if (safeMarketMood >= -0.4) {
	palette = [
	  "rgba(90, 15, 230, 0.9)",
	  "rgba(80, 0, 235, 0.85)",
	  "rgba(70, 0, 240, 0.8)",
	  "rgba(60, 0, 245, 0.78)",
	  "rgba(50, 0, 250, 0.75)",
	  "rgba(40, 10, 250, 0.7)",
	  "rgba(30, 15, 252, 0.65)",
	  "rgba(20, 20, 255, 0.6)"
	];
  } else if (safeMarketMood >= -0.45) {
	palette = [
	  "rgba(80, 10, 235, 0.9)",
	  "rgba(70, 0, 240, 0.85)",
	  "rgba(60, 0, 245, 0.8)",
	  "rgba(50, 0, 250, 0.78)",
	  "rgba(40, 0, 252, 0.75)",
	  "rgba(30, 5, 254, 0.7)",
	  "rgba(20, 10, 255, 0.65)",
	  "rgba(10, 15, 255, 0.6)"
	];
  } else if (safeMarketMood >= -0.5) {
	palette = [
	  "rgba(70, 5, 240, 0.9)",
	  "rgba(60, 0, 245, 0.85)",
	  "rgba(50, 0, 250, 0.8)",
	  "rgba(40, 0, 252, 0.78)",
	  "rgba(30, 0, 254, 0.75)",
	  "rgba(20, 0, 255, 0.7)",
	  "rgba(10, 5, 255, 0.65)",
	  "rgba(0, 10, 255, 0.6)"
	];
  } else if (safeMarketMood >= -0.6) {
	palette = [
	  "rgba(60, 0, 245, 0.9)",
	  "rgba(50, 0, 250, 0.85)",
	  "rgba(40, 0, 252, 0.8)",
	  "rgba(30, 0, 254, 0.78)",
	  "rgba(20, 0, 255, 0.75)",
	  "rgba(10, 0, 255, 0.7)",
	  "rgba(5, 0, 255, 0.65)",
	  "rgba(0, 0, 255, 0.6)"
	];
  } else if (safeMarketMood >= -0.7) {
	palette = [
	  "rgba(50, 0, 240, 0.9)",
	  "rgba(40, 0, 245, 0.85)",
	  "rgba(30, 0, 250, 0.8)",
	  "rgba(20, 0, 252, 0.78)",
	  "rgba(10, 0, 255, 0.75)",
	  "rgba(5, 0, 255, 0.7)",
	  "rgba(0, 0, 255, 0.65)",
	  "rgba(0, 10, 230, 0.6)"
	];
  } else if (safeMarketMood >= -0.8) {
	palette = [
	  "rgba(40, 0, 235, 0.9)",
	  "rgba(30, 0, 240, 0.85)",
	  "rgba(20, 0, 245, 0.8)",
	  "rgba(10, 0, 250, 0.78)",
	  "rgba(5, 0, 255, 0.75)",
	  "rgba(0, 0, 255, 0.7)",
	  "rgba(0, 10, 230, 0.65)",
	  "rgba(0, 20, 210, 0.6)"
	];
  } else if (safeMarketMood >= -0.9) {
	palette = [
	  "rgba(30, 0, 230, 0.9)",
	  "rgba(20, 0, 235, 0.85)",
	  "rgba(10, 0, 240, 0.8)",
	  "rgba(5, 0, 245, 0.78)",
	  "rgba(0, 0, 250, 0.75)",
	  "rgba(0, 10, 230, 0.7)",
	  "rgba(0, 20, 210, 0.65)",
	  "rgba(0, 30, 190, 0.6)"
	];
  } else if (safeMarketMood >= -1.0) {
	palette = [
	  "rgba(20, 0, 225, 0.95)",
	  "rgba(10, 0, 230, 0.9)",
	  "rgba(5, 0, 235, 0.85)",
	  "rgba(0, 0, 240, 0.8)",
	  "rgba(0, 5, 230, 0.75)",
	  "rgba(0, 15, 210, 0.7)",
	  "rgba(0, 25, 190, 0.65)",
	  "rgba(0, 35, 170, 0.6)"
	];
  } else if (safeMarketMood >= -1.1) {
	palette = [
	  "rgba(18, 0, 222, 0.95)",
	  "rgba(8, 0, 227, 0.9)",
	  "rgba(3, 0, 232, 0.85)",
	  "rgba(0, 0, 237, 0.8)",
	  "rgba(0, 8, 227, 0.75)",
	  "rgba(0, 18, 207, 0.7)",
	  "rgba(0, 28, 187, 0.65)",
	  "rgba(0, 38, 167, 0.6)"
	];
  } else if (safeMarketMood >= -1.2) {
	palette = [
	  "rgba(15, 0, 220, 0.95)",
	  "rgba(5, 0, 225, 0.9)",
	  "rgba(0, 0, 230, 0.85)",
	  "rgba(0, 5, 235, 0.8)",
	  "rgba(0, 15, 220, 0.75)",
	  "rgba(0, 25, 200, 0.7)",
	  "rgba(0, 35, 180, 0.65)",
	  "rgba(0, 45, 160, 0.6)"
	];
  } else if (safeMarketMood >= -1.3) {
	palette = [
	  "rgba(10, 0, 218, 0.95)",
	  "rgba(5, 0, 223, 0.9)",
	  "rgba(0, 0, 228, 0.85)",
	  "rgba(0, 8, 233, 0.8)",
	  "rgba(0, 18, 218, 0.75)",
	  "rgba(0, 28, 198, 0.7)",
	  "rgba(0, 38, 178, 0.65)",
	  "rgba(0, 48, 158, 0.6)"
	];
  } else if (safeMarketMood >= -1.4) {
	palette = [
	  "rgba(8, 0, 216, 0.95)",
	  "rgba(3, 0, 221, 0.9)",
	  "rgba(0, 0, 226, 0.85)",
	  "rgba(0, 10, 231, 0.8)",
	  "rgba(0, 20, 216, 0.75)",
	  "rgba(0, 30, 196, 0.7)",
	  "rgba(0, 40, 176, 0.65)",
	  "rgba(0, 50, 156, 0.6)"
	];
  } else if (safeMarketMood >= -1.5) {
	palette = [
	  "rgba(5, 0, 214, 0.95)",
	  "rgba(0, 0, 219, 0.9)",
	  "rgba(0, 5, 224, 0.85)",
	  "rgba(0, 15, 229, 0.8)",
	  "rgba(0, 25, 214, 0.75)",
	  "rgba(0, 35, 194, 0.7)",
	  "rgba(0, 45, 174, 0.65)",
	  "rgba(0, 55, 154, 0.6)"
	];
  } else if (safeMarketMood >= -1.6) {
	palette = [
	  "rgba(3, 0, 212, 0.95)",
	  "rgba(0, 0, 217, 0.9)",
	  "rgba(0, 8, 222, 0.85)",
	  "rgba(0, 18, 227, 0.8)",
	  "rgba(0, 28, 212, 0.75)",
	  "rgba(0, 38, 192, 0.7)",
	  "rgba(0, 48, 172, 0.65)",
	  "rgba(0, 58, 152, 0.6)"
	];
  } else if (safeMarketMood >= -1.7) {
	palette = [
	  "rgba(0, 0, 210, 0.95)",
	  "rgba(0, 5, 215, 0.9)",
	  "rgba(0, 10, 220, 0.85)",
	  "rgba(0, 20, 225, 0.8)",
	  "rgba(0, 30, 210, 0.75)",
	  "rgba(0, 40, 190, 0.7)",
	  "rgba(0, 50, 170, 0.65)",
	  "rgba(0, 60, 150, 0.6)"
	];
  } else if (safeMarketMood >= -1.8) {
	palette = [
	  "rgba(0, 0, 208, 0.95)",
	  "rgba(0, 8, 213, 0.9)",
	  "rgba(0, 15, 218, 0.85)",
	  "rgba(0, 25, 223, 0.8)",
	  "rgba(0, 35, 208, 0.75)",
	  "rgba(0, 45, 188, 0.7)",
	  "rgba(0, 55, 168, 0.65)",
	  "rgba(0, 65, 148, 0.6)"
	];
  } else if (safeMarketMood >= -1.9) {
	palette = [
	  "rgba(0, 0, 205, 0.95)",
	  "rgba(0, 10, 210, 0.9)",
	  "rgba(0, 20, 215, 0.85)",
	  "rgba(0, 30, 220, 0.8)",
	  "rgba(0, 40, 205, 0.75)",
	  "rgba(0, 50, 185, 0.7)",
	  "rgba(0, 60, 165, 0.65)",
	  "rgba(0, 70, 145, 0.6)"
	];
  } else if (safeMarketMood >= -2.0) {
	palette = [
	  "rgba(0, 0, 200, 0.95)",
	  "rgba(0, 15, 205, 0.9)",
	  "rgba(0, 25, 210, 0.85)",
	  "rgba(0, 35, 215, 0.8)",
	  "rgba(0, 45, 200, 0.75)",
	  "rgba(0, 55, 180, 0.7)",
	  "rgba(0, 65, 160, 0.65)",
	  "rgba(0, 75, 140, 0.6)"
	];
  }  else if (safeMarketMood >= -2.1) {
	palette = [
	  "rgba(0, 5, 195, 0.95)",
	  "rgba(0, 20, 200, 0.9)",
	  "rgba(0, 30, 205, 0.85)",
	  "rgba(0, 40, 210, 0.8)",
	  "rgba(0, 50, 195, 0.75)",
	  "rgba(0, 60, 175, 0.7)",
	  "rgba(0, 70, 155, 0.65)",
	  "rgba(0, 80, 135, 0.6)"
	];
  } else if (safeMarketMood >= -2.2) {
	palette = [
	  "rgba(0, 10, 190, 0.95)",
	  "rgba(0, 25, 195, 0.9)",
	  "rgba(0, 35, 200, 0.85)",
	  "rgba(0, 45, 205, 0.8)",
	  "rgba(0, 55, 190, 0.75)",
	  "rgba(0, 65, 170, 0.7)",
	  "rgba(0, 75, 150, 0.65)",
	  "rgba(0, 85, 130, 0.6)"
	];
  } else if (safeMarketMood >= -2.3) {
	palette = [
	  "rgba(0, 15, 185, 0.95)",
	  "rgba(0, 30, 190, 0.9)",
	  "rgba(0, 40, 195, 0.85)",
	  "rgba(0, 50, 200, 0.8)",
	  "rgba(0, 60, 185, 0.75)",
	  "rgba(0, 70, 165, 0.7)",
	  "rgba(0, 80, 145, 0.65)",
	  "rgba(0, 90, 125, 0.6)"
	];
  } else if (safeMarketMood >= -2.4) {
	palette = [
	  "rgba(0, 20, 180, 0.95)",
	  "rgba(0, 35, 185, 0.9)",
	  "rgba(0, 45, 190, 0.85)",
	  "rgba(0, 55, 195, 0.8)",
	  "rgba(0, 65, 180, 0.75)",
	  "rgba(0, 75, 160, 0.7)",
	  "rgba(0, 85, 140, 0.65)",
	  "rgba(0, 95, 120, 0.6)"
	];
  } else if (safeMarketMood >= -2.5) {
	palette = [
	  "rgba(0, 25, 175, 0.95)",
	  "rgba(0, 40, 180, 0.9)",
	  "rgba(0, 50, 185, 0.85)",
	  "rgba(0, 60, 190, 0.8)",
	  "rgba(0, 70, 175, 0.75)",
	  "rgba(0, 80, 155, 0.7)",
	  "rgba(0, 90, 135, 0.65)",
	  "rgba(0, 100, 115, 0.6)"
	];
  } else if (safeMarketMood >= -2.6) {
	palette = [
	  "rgba(0, 30, 170, 0.95)",
	  "rgba(0, 45, 175, 0.9)",
	  "rgba(0, 55, 180, 0.85)",
	  "rgba(0, 65, 185, 0.8)",
	  "rgba(0, 75, 170, 0.75)",
	  "rgba(0, 85, 150, 0.7)",
	  "rgba(0, 95, 130, 0.65)",
	  "rgba(0, 105, 110, 0.6)"
	];
  } else if (safeMarketMood >= -2.7) {
	palette = [
	  "rgba(0, 35, 165, 0.95)",
	  "rgba(0, 50, 170, 0.9)",
	  "rgba(0, 60, 175, 0.85)",
	  "rgba(0, 70, 180, 0.8)",
	  "rgba(0, 80, 165, 0.75)",
	  "rgba(0, 90, 145, 0.7)",
	  "rgba(0, 100, 125, 0.65)",
	  "rgba(0, 110, 105, 0.6)"
	];
  } else if (safeMarketMood >= -2.8) {
	palette = [
	  "rgba(0, 40, 160, 0.95)",
	  "rgba(0, 55, 165, 0.9)",
	  "rgba(0, 65, 170, 0.85)",
	  "rgba(0, 75, 175, 0.8)",
	  "rgba(0, 85, 160, 0.75)",
	  "rgba(0, 95, 140, 0.7)",
	  "rgba(0, 105, 120, 0.65)",
	  "rgba(0, 115, 100, 0.6)"
	];
  } else if (safeMarketMood >= -2.9) {
	palette = [
	  "rgba(0, 45, 155, 0.95)",
	  "rgba(0, 60, 160, 0.9)",
	  "rgba(0, 70, 165, 0.85)",
	  "rgba(0, 80, 170, 0.8)",
	  "rgba(0, 90, 155, 0.75)",
	  "rgba(0, 100, 135, 0.7)",
	  "rgba(0, 110, 115, 0.65)",
	  "rgba(0, 120, 95, 0.6)"
	];
  } else if (safeMarketMood >= -3.0) {
	palette = [
	  "rgba(0, 50, 150, 0.95)",
	  "rgba(0, 65, 155, 0.9)",
	  "rgba(0, 75, 160, 0.85)",
	  "rgba(0, 85, 165, 0.8)",
	  "rgba(0, 95, 150, 0.75)",
	  "rgba(0, 105, 130, 0.7)",
	  "rgba(0, 115, 110, 0.65)",
	  "rgba(0, 125, 90, 0.6)"
	];
  } else if (safeMarketMood >= -3.1) {
	palette = [
	  "rgba(0, 55, 145, 0.95)",
	  "rgba(0, 70, 150, 0.9)",
	  "rgba(0, 80, 155, 0.85)",
	  "rgba(0, 90, 160, 0.8)",
	  "rgba(0, 100, 145, 0.75)",
	  "rgba(0, 110, 125, 0.7)",
	  "rgba(0, 120, 105, 0.65)",
	  "rgba(0, 130, 85, 0.6)"
	];
  } else if (safeMarketMood >= -3.2) {
	palette = [
	  "rgba(0, 60, 140, 0.95)",
	  "rgba(0, 75, 145, 0.9)",
	  "rgba(0, 85, 150, 0.85)",
	  "rgba(0, 95, 155, 0.8)",
	  "rgba(0, 105, 140, 0.75)",
	  "rgba(0, 115, 120, 0.7)",
	  "rgba(0, 125, 100, 0.65)",
	  "rgba(0, 135, 80, 0.6)"
	];
  } else if (safeMarketMood >= -3.3) {
	palette = [
	  "rgba(0, 65, 135, 0.95)",
	  "rgba(0, 80, 140, 0.9)",
	  "rgba(0, 90, 145, 0.85)",
	  "rgba(0, 100, 150, 0.8)",
	  "rgba(0, 110, 135, 0.75)",
	  "rgba(0, 120, 115, 0.7)",
	  "rgba(0, 130, 95, 0.65)",
	  "rgba(0, 140, 75, 0.6)"
	];
  } else if (safeMarketMood >= -3.4) {
	palette = [
	  "rgba(0, 70, 130, 0.95)",
	  "rgba(0, 85, 135, 0.9)",
	  "rgba(0, 95, 140, 0.85)",
	  "rgba(0, 105, 145, 0.8)",
	  "rgba(0, 115, 130, 0.75)",
	  "rgba(0, 125, 110, 0.7)",
	  "rgba(0, 135, 90, 0.65)",
	  "rgba(0, 145, 70, 0.6)"
	];
  } else if (safeMarketMood >= -3.5) {
	palette = [
	  "rgba(0, 75, 125, 0.95)",
	  "rgba(0, 90, 130, 0.9)",
	  "rgba(0, 100, 135, 0.85)",
	  "rgba(0, 110, 140, 0.8)",
	  "rgba(0, 120, 125, 0.75)",
	  "rgba(0, 130, 105, 0.7)",
	  "rgba(0, 140, 85, 0.65)",
	  "rgba(0, 150, 65, 0.6)"
	];
  } else if (safeMarketMood >= -3.6) {
	palette = [
	  "rgba(0, 80, 120, 0.95)",
	  "rgba(0, 95, 125, 0.9)",
	  "rgba(0, 105, 130, 0.85)",
	  "rgba(0, 115, 135, 0.8)",
	  "rgba(0, 125, 120, 0.75)",
	  "rgba(0, 135, 100, 0.7)",
	  "rgba(0, 145, 80, 0.65)",
	  "rgba(0, 155, 60, 0.6)"
	];
  } else if (safeMarketMood >= -3.7) {
	palette = [
	  "rgba(0, 85, 115, 0.95)",
	  "rgba(0, 100, 120, 0.9)",
	  "rgba(0, 110, 125, 0.85)",
	  "rgba(0, 120, 130, 0.8)",
	  "rgba(0, 130, 115, 0.75)",
	  "rgba(0, 140, 95, 0.7)",
	  "rgba(0, 150, 75, 0.65)",
	  "rgba(0, 160, 55, 0.6)"
	];
  } else if (safeMarketMood >= -3.8) {
	palette = [
	  "rgba(0, 90, 110, 0.95)",
	  "rgba(0, 105, 115, 0.9)",
	  "rgba(0, 115, 120, 0.85)",
	  "rgba(0, 125, 125, 0.8)",
	  "rgba(0, 135, 110, 0.75)",
	  "rgba(0, 145, 90, 0.7)",
	  "rgba(0, 155, 70, 0.65)",
	  "rgba(0, 165, 50, 0.6)"
	];
  } else if (safeMarketMood >= -3.9) {
	palette = [
	  "rgba(0, 95, 105, 0.95)",
	  "rgba(0, 110, 110, 0.9)",
	  "rgba(0, 120, 115, 0.85)",
	  "rgba(0, 130, 120, 0.8)",
	  "rgba(0, 140, 105, 0.75)",
	  "rgba(0, 150, 85, 0.7)",
	  "rgba(0, 160, 65, 0.65)",
	  "rgba(0, 170, 45, 0.6)"
	];
  } else if (safeMarketMood >= -4.0) {
	palette = [
	  "rgba(0, 100, 100, 0.95)",
	  "rgba(0, 115, 105, 0.9)",
	  "rgba(0, 125, 110, 0.85)",
	  "rgba(0, 135, 115, 0.8)",
	  "rgba(0, 145, 100, 0.75)",
	  "rgba(0, 155, 80, 0.7)",
	  "rgba(0, 165, 60, 0.65)",
	  "rgba(0, 175, 40, 0.6)"
	];
  } else if (safeMarketMood >= -4.1) {
	palette = [
	  "rgba(0, 105, 95, 0.95)",
	  "rgba(0, 120, 100, 0.9)",
	  "rgba(0, 130, 105, 0.85)",
	  "rgba(0, 140, 110, 0.8)",
	  "rgba(0, 150, 95, 0.75)",
	  "rgba(0, 160, 75, 0.7)",
	  "rgba(0, 170, 55, 0.65)",
	  "rgba(0, 180, 35, 0.6)"
	];
  } else if (safeMarketMood >= -4.2) {
	palette = [
	  "rgba(0, 110, 90, 0.95)",
	  "rgba(0, 125, 95, 0.9)",
	  "rgba(0, 135, 100, 0.85)",
	  "rgba(0, 145, 105, 0.8)",
	  "rgba(0, 155, 90, 0.75)",
	  "rgba(0, 165, 70, 0.7)",
	  "rgba(0, 175, 50, 0.65)",
	  "rgba(0, 185, 30, 0.6)"
	];
  } else if (safeMarketMood >= -4.3) {
	palette = [
	  "rgba(0, 115, 85, 0.95)",
	  "rgba(0, 130, 90, 0.9)",
	  "rgba(0, 140, 95, 0.85)",
	  "rgba(0, 150, 100, 0.8)",
	  "rgba(0, 160, 85, 0.75)",
	  "rgba(0, 170, 65, 0.7)",
	  "rgba(0, 180, 45, 0.65)",
	  "rgba(0, 190, 25, 0.6)"
	];
  } else if (safeMarketMood >= -4.4) {
	palette = [
	  "rgba(0, 120, 80, 0.95)",
	  "rgba(0, 135, 85, 0.9)",
	  "rgba(0, 145, 90, 0.85)",
	  "rgba(0, 155, 95, 0.8)",
	  "rgba(0, 165, 80, 0.75)",
	  "rgba(0, 175, 60, 0.7)",
	  "rgba(0, 185, 40, 0.65)",
	  "rgba(0, 195, 20, 0.6)"
	];
  } else if (safeMarketMood >= -4.5) {
	palette = [
	  "rgba(0, 125, 75, 0.95)",
	  "rgba(0, 140, 80, 0.9)",
	  "rgba(0, 150, 85, 0.85)",
	  "rgba(0, 160, 90, 0.8)",
	  "rgba(0, 170, 75, 0.75)",
	  "rgba(0, 180, 55, 0.7)",
	  "rgba(0, 190, 35, 0.65)",
	  "rgba(0, 200, 15, 0.6)"
	];
  } else if (safeMarketMood >= -4.6) {
	palette = [
	  "rgba(0, 130, 70, 0.95)",
	  "rgba(0, 145, 75, 0.9)",
	  "rgba(0, 155, 80, 0.85)",
	  "rgba(0, 165, 85, 0.8)",
	  "rgba(0, 175, 70, 0.75)",
	  "rgba(0, 185, 50, 0.7)",
	  "rgba(0, 195, 30, 0.65)",
	  "rgba(0, 205, 10, 0.6)"
	];
  } else if (safeMarketMood >= -4.7) {
	palette = [
	  "rgba(0, 135, 65, 0.95)",
	  "rgba(0, 150, 70, 0.9)",
	  "rgba(0, 160, 75, 0.85)",
	  "rgba(0, 170, 80, 0.8)",
	  "rgba(0, 180, 65, 0.75)",
	  "rgba(0, 190, 45, 0.7)",
	  "rgba(0, 200, 25, 0.65)",
	  "rgba(0, 210, 5, 0.6)"
	];
  } else if (safeMarketMood >= -4.8) {
	palette = [
	  "rgba(0, 140, 60, 0.95)",
	  "rgba(0, 155, 65, 0.9)",
	  "rgba(0, 165, 70, 0.85)",
	  "rgba(0, 175, 75, 0.8)",
	  "rgba(0, 185, 60, 0.75)",
	  "rgba(0, 195, 40, 0.7)",
	  "rgba(0, 205, 20, 0.65)",
	  "rgba(0, 215, 0, 0.6)"
	];
  } else if (safeMarketMood >= -4.9) {
	palette = [
	  "rgba(0, 145, 55, 0.95)",
	  "rgba(0, 160, 60, 0.9)",
	  "rgba(0, 170, 65, 0.85)",
	  "rgba(0, 180, 70, 0.8)",
	  "rgba(0, 190, 55, 0.75)",
	  "rgba(0, 200, 35, 0.7)",
	  "rgba(0, 210, 15, 0.65)",
	  "rgba(0, 220, 0, 0.6)"
	];
  } else if (safeMarketMood >= -5.0) {
	palette = [
	  "rgba(0, 150, 50, 0.95)",
	  "rgba(0, 165, 55, 0.9)",
	  "rgba(0, 175, 60, 0.85)",
	  "rgba(0, 185, 65, 0.8)",
	  "rgba(0, 195, 50, 0.75)",
	  "rgba(0, 205, 30, 0.7)",
	  "rgba(0, 215, 10, 0.65)",
	  "rgba(0, 225, 0, 0.6)"
	];
  }
  
  // ---- Enhanced user mood intensity impact on palette ----
  // User mood now has more influence on the palette colors
  const userInfluence = Math.abs(userMood) / 2; // Scale to half the maximum influence
  
  if (userMood > 0) {
	// For positive user mood: Brighten and shift toward yellows/oranges
	palette = palette.map(color => {
	  let rgbaMatch = color.match(/rgba\((\d+),\s*(\d+),\s*(\d+),\s*([\d.]+)\)/);
	  if (!rgbaMatch) return color;
	  
	  let r = parseInt(rgbaMatch[1]);
	  let g = parseInt(rgbaMatch[2]);
	  let b = parseInt(rgbaMatch[3]);
	  let a = parseFloat(rgbaMatch[4]);
	  
	  // Brighten reds and greens more than blues for a warmer tone
	  r = Math.min(255, r + Math.round(userInfluence * 30));
	  g = Math.min(255, g + Math.round(userInfluence * 25));
	  b = Math.min(255, b + Math.round(userInfluence * 10));
	  
	  return `rgba(${r}, ${g}, ${b}, ${a})`;
	});
  } else if (userMood < 0) {
	// For negative user mood: Deepen and shift toward blues/purples
	palette = palette.map(color => {
	  let rgbaMatch = color.match(/rgba\((\d+),\s*(\d+),\s*(\d+),\s*([\d.]+)\)/);
	  if (!rgbaMatch) return color;
	  
	  let r = parseInt(rgbaMatch[1]);
	  let g = parseInt(rgbaMatch[2]);
	  let b = parseInt(rgbaMatch[3]);
	  let a = parseFloat(rgbaMatch[4]);
	  
	  // Increase blues, decrease reds and greens for a cooler tone
	  r = Math.max(0, r - Math.round(userInfluence * 20));
	  g = Math.max(0, g - Math.round(userInfluence * 15));
	  b = Math.min(255, b + Math.round(userInfluence * 30));
	  
	  return `rgba(${r}, ${g}, ${b}, ${a})`;
	});
  }
  
  // ---- Market-User Mood Relationship ----
  // Add special coloration for situations where market and user moods diverge significantly
  const moodDivergence = marketMood * userMood;
  
  if (moodDivergence < -1) {
	// Strong negative correlation (user and market in opposite directions)
	// Add contrasting highlights to the palette
	const contrastColor = userMood > 0 
	  ? "rgba(255, 255, 50, 0.7)" // Bright yellow highlight if user is positive
	  : "rgba(50, 50, 255, 0.7)"; // Bright blue highlight if user is negative
	
	palette.push(contrastColor);
  }
  
  return palette;
}
	  

// Helper functions for color manipulation
function lightenColor(color, percent) {
  let rgbaMatch = color.match(/rgba\((\d+),\s*(\d+),\s*(\d+),\s*([\d.]+)\)/);
  if (!rgbaMatch) return color;
  
  let r = parseInt(rgbaMatch[1]);
  let g = parseInt(rgbaMatch[2]);
  let b = parseInt(rgbaMatch[3]);
  let a = parseFloat(rgbaMatch[4]);
  
  r = Math.min(255, r + Math.round(2.55 * percent));
  g = Math.min(255, g + Math.round(2.55 * percent));
  b = Math.min(255, b + Math.round(2.55 * percent));
  
  return `rgba(${r}, ${g}, ${b}, ${a})`;
}

function darkenColor(color, percent) {
  let rgbaMatch = color.match(/rgba\((\d+),\s*(\d+),\s*(\d+),\s*([\d.]+)\)/);
  if (!rgbaMatch) return color;
  
  let r = parseInt(rgbaMatch[1]);
  let g = parseInt(rgbaMatch[2]);
  let b = parseInt(rgbaMatch[3]);
  let a = parseFloat(rgbaMatch[4]);
  
  r = Math.max(0, r - Math.round(2.55 * percent));
  g = Math.max(0, g - Math.round(2.55 * percent));
  b = Math.max(0, b - Math.round(2.55 * percent));
  
  return `rgba(${r}, ${g}, ${b}, ${a})`;
}

function startMoodUpdates(interval = 10000) {
  if (updateTimer) clearInterval(updateTimer);
  console.log(`🔄 Starting mood update timer with interval: ${interval}ms`);
  
  // Run immediately
  initMoodPage();
  
  // Then set interval
  updateTimer = setInterval(async () => {
	console.log("🔄 Periodic update of mood data...");
	await initMoodPage();
  }, interval);
}

function calculateUserMood() {
  // Get 5 most recent transactions from moodHistory
  const recentHistory = [...moodHistory].sort((a, b) => 
	new Date(b.timestamp) - new Date(a.timestamp)
  ).slice(0, 5);
  
  // Apply weighted calculation - most recent has more impact
  let weightedMood = 0;
  let totalWeight = 0;
  
  recentHistory.forEach((entry, index) => {
	const weight = 5 - index; // 5 for most recent, 1 for oldest
	const entryMood = (entry.positiveImpact || 0) - (entry.negativeImpact || 0);
	weightedMood += entryMood * weight;
	totalWeight += weight;
  });
  
  return totalWeight > 0 ? weightedMood / totalWeight : 0;
}

document.addEventListener("DOMContentLoaded", async () => {
  // mood.js is pulled into every page's bundle (socketU imports
  // showMoodAnalysisPopup), so only initialize on the actual mood board page —
  // otherwise the "Session ID is missing" toast flashes on index/login.
  if (!document.getElementById("moodScoresContainer")) return;
  try {
	console.log("🚀 Initializing mood interface...");

	sessionId = localStorage.getItem("sessionId");
	if (!sessionId) {
	  console.error("❌ No session ID found in localStorage.");
	  showNotification("Session ID is missing. Please log in.", false);
	  return;
	}
	if (!moodSocket || !(
		  (typeof moodSocket.connected !== 'undefined' && moodSocket.connected) || 
		  (typeof moodSocket.readyState !== 'undefined' && moodSocket.readyState === WebSocket.OPEN)
		)) {
		console.log("🔌 Establishing WebSocket connection...");
		connectMoodSocket();
	  }
	await loadP5();
	await initMoodPage();
	
	// Initialize visualization
	initP5Sketch();

	// Test the burst functionality after initialization
	setTimeout(() => {
	  console.log("🧪 Testing burst mechanism...");
	  triggerBurst(3.5); // Test with a strong positive mood
	}, 3000);
	
	// Start periodic data updates
	startMoodUpdates(15000);
	
	console.log("✅ Mood interface initialization complete");
  } catch (error) {
	console.error("❌ Error during initialization:", error);
	showNotification("Error initializing mood display. Using fallback data.", false);
	
	// Use fallback data
	const fallbackData = generateFallbackMoodData();
	userMoodScore = fallbackData.userMood.averageMood * MOOD_AMPLIFICATION_FACTOR;
	marketMoodScore = fallbackData.marketMood.averageMood * MOOD_AMPLIFICATION_FACTOR;
	displayMoodScores();
	initP5Sketch();
	// Start updates with longer interval when in fallback mode
	startMoodUpdates(30000);
  }
});

// Add a debug flag to globals to allow manual burst triggering from console
window.debugMood = {
  triggerBurst: (score) => {
	console.log("🔧 Manual burst triggered with score:", score);
	triggerBurst(score || 4);
  },
  checkSocket: () => {
	console.log("🔌 Socket status:", socket ? 
	  (socket.connected ? "Connected ✅" : "Disconnected ❌") 
	  : "Not initialized ⚠️");
	return socket;
  },
  listenerTest: () => {
	// Test if the transactionBurst listener exists
	const hasListener = socket && socket._callbacks && 
	  socket._callbacks['$transactionBurst'] && 
	  socket._callbacks['$transactionBurst'].length > 0;
	
	console.log("🔈 Transaction burst listener:", hasListener ? "Active ✅" : "Not found ❌");
	
	// If no listener, try to set it up
	if (!hasListener && socket) {
	  console.log("🔄 Attempting to re-establish burst listener");
	  setupBurstListener();
	}
	
	return hasListener;
  }
};

// --- Redirect guard: only forward from stockTransaction.html
window.isOn ||= (file) =>
  location.pathname.endsWith(`/${file}`) || location.pathname.endsWith(file);

window.shouldRedirectToMarket ||= () => window.isOn('stockTransaction.html');

window.doMarketRedirectIfNeeded ||= () => {
  if (window.shouldRedirectToMarket()) window.location.href = 'market.html';
};



// Helper to show notifications
function showNotification(message, isSuccess = true) {
  console.log(`${isSuccess ? '✅' : '❌'} ${message}`);
  
  // Create or get notification element
  let notificationElement = document.getElementById('moodNotification');
  if (!notificationElement) {
	notificationElement = document.createElement('div');
	notificationElement.id = 'moodNotification';
	Object.assign(notificationElement.style, {
	  position: 'fixed',
	  top: '10px',
	  left: '50%',
	  transform: 'translateX(-50%)',
	  padding: '8px 16px',
	  borderRadius: '3px',
	  fontSize: '14px',
	  fontFamily: 'sans-serif',
	  zIndex: '10001',
	  opacity: '0',
	  transition: 'opacity 0.3s ease'
	});
	document.body.appendChild(notificationElement);
  }
  
  // Set styles based on success/error
  Object.assign(notificationElement.style, {
	backgroundColor: isSuccess ? 'rgba(76, 175, 80, 0.9)' : 'rgba(244, 67, 54, 0.9)',
	color: 'white',
	boxShadow: `0 2px 10px ${isSuccess ? 'rgba(76, 175, 80, 0.5)' : 'rgba(244, 67, 54, 0.5)'}`
  });
  
  notificationElement.textContent = message;
  notificationElement.style.opacity = '1';
  
  // Hide after 4 seconds
  setTimeout(() => {
	notificationElement.style.opacity = '0';
  }, 4000);
}

// Show mood analysis popup with smoky hologram bg + non-repeating random + grid
export function showMoodAnalysisPopup(analysis) {
  console.log("📌 showMoodAnalysisPopup called with:", analysis);

  if (!analysis || !analysis.analysisTitle) {
	console.error("❌ Invalid analysis data:", analysis);
	return;
  }

  // ---- Pick main commentary safely ----
  const marketText  = (analysis.marketCommentary || "").trim();
  const summaryText = (analysis.analysisSummary  || "").trim();
  const detailsText = (analysis.detailedDiscussion || "").trim();

  let primaryText = marketText || summaryText || detailsText || "No analysis available.";
  const hasDistinctSummary = summaryText && summaryText !== primaryText;
  const hasDistinctDetails = detailsText && detailsText !== primaryText;
  const onlyOneSection = !hasDistinctSummary && !hasDistinctDetails;

  // ---- Smoky hologram palette (layered, dark, neon-friendly) ----
  const HOLOGRAM_BACKGROUNDS = [
	`
	  radial-gradient(1200px 600px at -10% -20%, rgba(0,255,255,0.14), transparent 60%),
	  radial-gradient(900px 500px  at 120% 130%, rgba(255,0,160,0.12), transparent 60%),
	  radial-gradient(700px 400px  at 100% -10%, rgba(0,200,255,0.10), transparent 60%),
	  linear-gradient(to bottom right, rgba(8,10,22,0.96), rgba(5,7,18,0.94))
	`,
	`
	  radial-gradient(1000px 500px at 10% 110%, rgba(0,255,200,0.10), transparent 65%),
	  radial-gradient(1000px 600px at 90% -10%, rgba(170,0,255,0.12), transparent 60%),
	  radial-gradient(700px 500px  at -10% 30%, rgba(0,150,255,0.08), transparent 60%),
	  linear-gradient(145deg, rgba(7,9,20,0.96), rgba(3,5,14,0.94))
	`,
	`
	  radial-gradient(800px 400px  at 0% 0%,   rgba(255,255,120,0.10), transparent 60%),
	  radial-gradient(1200px 700px at 100% 100%, rgba(0,220,255,0.12), transparent 60%),
	  radial-gradient(900px 600px  at 50% -20%, rgba(255,0,200,0.08), transparent 60%),
	  linear-gradient(160deg, rgba(8,12,24,0.96), rgba(4,6,12,0.94))
	`,
	`
	  radial-gradient(1000px 520px at -15% 30%, rgba(0,180,255,0.12), transparent 65%),
	  radial-gradient(900px 520px  at 115% 80%, rgba(0,255,220,0.10), transparent 60%),
	  radial-gradient(800px 420px  at 50% -20%, rgba(255,0,180,0.08), transparent 65%),
	  linear-gradient(150deg, rgba(6,8,18,0.96), rgba(3,5,12,0.95))
	`
  ];

  // ---- GUARANTEE variety: no immediate repeat, plus per-popup hue variance for mist ----
  const ROT_KEY = "__mood_bg_idx";
  const prevIdx = typeof window !== "undefined" ? (window[ROT_KEY] ?? -1) : -1;
  let idx = Math.floor(Math.random() * HOLOGRAM_BACKGROUNDS.length);
  if (idx === prevIdx) idx = (idx + 1) % HOLOGRAM_BACKGROUNDS.length;
  if (typeof window !== "undefined") window[ROT_KEY] = idx;
  const chosenBg = HOLOGRAM_BACKGROUNDS[idx];

  // Per-popup randoms for extra differentiation (don’t touch text readability)
  const hue = Math.floor(Math.random() * 360);                  // mist hue
  const gridOpacity = (0.22 + Math.random() * 0.14).toFixed(2); // 0.22–0.36
  const gridMinor = [5, 6, 7][Math.floor(Math.random() * 3)];   // px
  const gridMajor = [20, 24, 28][Math.floor(Math.random() * 3)]; // px
  const gridLine = 0.5; // hairline

  // Subtle parallax drift speeds to avoid sameness
  const mistDur = (18 + Math.floor(Math.random() * 8)) + "s";   // 18–25s
  const gridDur = (16 + Math.floor(Math.random() * 6)) + "s";   // 16–21s

  // Create backdrop
  const backdrop = document.createElement("div");
  Object.assign(backdrop.style, {
	position: "fixed",
	top: "0",
	left: "0",
	width: "100vw",
	height: "100vh",
	backgroundColor: "rgba(0, 5, 29, 0.85)",
	zIndex: "9998",
	display: "flex",
	justifyContent: "center",
	alignItems: "center",
	backdropFilter: "blur(3px)",
	WebkitBackdropFilter: "blur(3px)",
	fontFamily: "'BitHigh', sans-serif",
	transition: "opacity 0.3s ease-in-out",
  });

  // Create popup container
  const popupDiv = document.createElement("div");
  Object.assign(popupDiv.style, {
	position: "relative",
	overflow: "hidden",
	backgroundColor: "rgba(6,8,18,0.92)",
	backgroundImage: chosenBg,
	borderRadius: "4px",
	border: "1px solid rgba(61, 210, 2, 0.729)",
	boxShadow: "0 0 30px rgba(0, 255, 255, 0.8), 0 0 60px rgba(255, 0, 127, 0.6)",
	padding: "20px",
	zIndex: "9999",
	width: "92vw",
	maxWidth: "480px",
	maxHeight: "80vh",
	display: "flex",
	flexDirection: "column",
	color: "#a0d8ff",
	fontFamily: "'BitHigh', sans-serif",
	textShadow: "0 0 8px rgba(0, 255, 255, 0.8)",
	transform: "scale(0.9)",
	opacity: "0",
	transition: "transform 0.25s ease-out, opacity 0.3s ease-out",
	animation: "neonPulse 7s ease-in-out infinite alternate",
	backdropFilter: "blur(3px)",
	WebkitBackdropFilter: "blur(3px)",
  });
  popupDiv.setAttribute("data-bg-id", String(idx)); // helpful for debugging

  // Inject styles (once)
  let neonStyle = document.getElementById("mood-neon-styles");
  if (!neonStyle) {
	neonStyle = document.createElement("style");
	neonStyle.id = "mood-neon-styles";
	neonStyle.textContent = `
	  @keyframes neonPulse {
		0%   { box-shadow: 0 0 18px rgba(0,255,255,0.55), 0 0 36px rgba(255,0,127,0.35); }
		50%  { box-shadow: 0 0 34px rgba(0,255,255,0.95), 0 0 78px rgba(255,0,127,0.75); }
		100% { box-shadow: 0 0 22px rgba(0,255,255,0.65), 0 0 44px rgba(255,0,127,0.45); }
	  }
	  @keyframes mistDrift {
		0%   { transform: translate3d(-2%, -1%, 0) scale(1); opacity: 0.55; }
		100% { transform: translate3d( 2%,  1%, 0) scale(1.04); opacity: 0.7; }
	  }
	  @keyframes gridFloat {
		0%   { transform: translate3d(0,0,0); }
		100% { transform: translate3d(2px,1px,0); }
	  }
	  @media (prefers-reduced-motion: reduce) {
		.mood-mist, .mood-grid { animation: none !important; }
	  }
	`;
	document.head.appendChild(neonStyle);
  }

  // --- Smoky hologram mist overlay (per-popup hue variance) ---
  const mist = document.createElement("div");
  mist.className = "mood-mist";
  Object.assign(mist.style, {
	position: "absolute",
	inset: 0,
	pointerEvents: "none",
	zIndex: 0,
	borderRadius: "inherit",
	backgroundImage: `
	  radial-gradient(60% 40% at 25% 20%, rgba(255,255,255,0.035), transparent 60%),
	  radial-gradient(45% 55% at 75% 75%, rgba(255,255,255,0.03),  transparent 65%),
	  radial-gradient(80% 60% at 130% -10%, rgba(0,255,220,0.045), transparent 70%)
	`,
	mixBlendMode: "screen",
	filter: `blur(14px) saturate(115%) hue-rotate(${hue}deg)`,
	animation: `mistDrift ${mistDur} linear infinite alternate`,
  });
  popupDiv.appendChild(mist);

  // --- Neon grid overlay (hairline ~0.5px), per-popup opacity & cell size ---
  const grid = document.createElement("div");
  grid.className = "mood-grid";
  Object.assign(grid.style, {
	position: "absolute",
	inset: 0,
	pointerEvents: "none",
	zIndex: 1,
	borderRadius: "inherit",
	opacity: String(gridOpacity),
	backgroundImage: `
	  repeating-linear-gradient(to right, rgba(0,255,255,0.10) 0, rgba(0,255,255,0.10) ${gridLine}px, transparent ${gridLine}px, transparent ${gridMajor}px),
	  repeating-linear-gradient(to bottom, rgba(0,255,255,0.10) 0, rgba(0,255,255,0.10) ${gridLine}px, transparent ${gridLine}px, transparent ${gridMajor}px),
	  repeating-linear-gradient(to right, rgba(255,0,127,0.06) 0, rgba(255,0,127,0.06) ${gridLine}px, transparent ${gridLine}px, transparent ${gridMinor}px),
	  repeating-linear-gradient(to bottom, rgba(255,0,127,0.06) 0, rgba(255,0,127,0.06) ${gridLine}px, transparent ${gridLine}px, transparent ${gridMinor}px)
	`,
	backgroundBlendMode: "screen",
	animation: `gridFloat ${gridDur} linear infinite alternate`,
  });
  popupDiv.appendChild(grid);

  // Header
  const header = document.createElement("div");
  Object.assign(header.style, {
	position: "relative",
	zIndex: 2,
	marginBottom: "12px",
	borderBottom: "1px solid rgba(160, 216, 255, 0.3)",
	paddingBottom: "8px",
	textAlign: "center",
  });

  const title = document.createElement("h2");
  title.textContent = analysis.analysisTitle || "Mood Analysis";
  Object.assign(title.style, {
	margin: "0",
	fontSize: "16px",
	fontWeight: "700",
	textTransform: "uppercase",
  });
  header.appendChild(title);

  // What this popup IS — so a first-time trader isn't lost. Inline-styled on
  // purpose: this popup can appear on pages that don't load floor.css.
  const explain = document.createElement("p");
  explain.textContent = t(
	"floor.exp_analysis",
	"This is the voice of the bourse — the market itself reacting to a trade that just happened on the floor. It's commentary, not a request: read it, close it, nothing is asked of you."
  );
  Object.assign(explain.style, {
	margin: "8px auto 0",
	maxWidth: "420px",
	padding: "7px 10px",
	textAlign: "left",
	fontSize: "11.5px",
	lineHeight: "1.55",
	letterSpacing: "0.5px",
	color: "#f5ecd8",
	borderLeft: "3px solid rgba(255, 179, 71, 0.9)",
	background: "rgba(255, 179, 71, 0.14)",
	borderRadius: "0 3px 3px 0",
	textShadow: "none",
  });
  header.appendChild(explain);

  // Content container (with subtle glass for readability)
  const contentContainer = document.createElement("div");
  Object.assign(contentContainer.style, {
	position: "relative",
	zIndex: 2,
	overflowY: "auto",
	flex: "1",
	paddingRight: "8px",
	maxHeight: "60vh",
	fontSize: "12px",
	lineHeight: "1.6",
	color: "#a0d8ff",
	background: "linear-gradient(to bottom, rgba(6,10,18,0.35), rgba(6,10,18,0.10))",
	borderRadius: "8px",
	padding: "10px 10px 8px 10px",
  });

  if (onlyOneSection) {
	const singleSection = document.createElement("div");
	singleSection.style.marginBottom = "18px";

	const singleLabel = document.createElement("h3");
	singleLabel.textContent = "Market";
	Object.assign(singleLabel.style, {
	  margin: "0 0 6px 0",
	  fontSize: "14px",
	  fontWeight: "600",
	  color: "#ff66cc",
	});

	const singleContent = document.createElement("p");
	singleContent.textContent = primaryText;
	singleContent.style.margin = "0";

	singleSection.appendChild(singleLabel);
	singleSection.appendChild(singleContent);
	contentContainer.appendChild(singleSection);
  } else {
	if (hasDistinctSummary) {
	  const summarySection = document.createElement("div");
	  summarySection.style.marginBottom = "18px";

	  const summaryLabel = document.createElement("h3");
	  summaryLabel.textContent = "Summary";
	  Object.assign(summaryLabel.style, {
		margin: "0 0 6px 0",
		fontSize: "14px",
		fontWeight: "600",
		color: "#ff66cc",
	  });

	  const summaryContent = document.createElement("p");
	  summaryContent.textContent = summaryText;
	  summaryContent.style.margin = "0";

	  summarySection.appendChild(summaryLabel);
	  summarySection.appendChild(summaryContent);
	  contentContainer.appendChild(summarySection);
	}

	if (hasDistinctDetails) {
	  const detailsSection = document.createElement("div");

	  const detailsLabel = document.createElement("h3");
	  detailsLabel.textContent = "Details";
	  Object.assign(detailsLabel.style, {
		margin: "0 0 6px 0",
		fontSize: "14px",
		fontWeight: "600",
		color: "#ff66cc",
	  });

	  const detailsContent = document.createElement("div");
	  detailsContent.textContent = detailsText;

	  detailsSection.appendChild(detailsLabel);
	  detailsSection.appendChild(detailsContent);
	  contentContainer.appendChild(detailsSection);
	}
  }

  // Footer with close
  const footer = document.createElement("div");
  Object.assign(footer.style, {
	position: "relative",
	zIndex: 2,
	marginTop: "15px",
	borderTop: "1px solid rgba(255, 255, 255, 0.2)",
	paddingTop: "12px",
	textAlign: "center",
  });

  const closeButton = document.createElement("button");
  closeButton.textContent = "Close";
  Object.assign(closeButton.style, {
	padding: "12px 20px",
	backgroundColor: "rgba(255, 0, 0, 0.75)",
	color: "rgba(253, 254, 0, 0.9)",
	border: "none",
	borderRadius: "3px",
	cursor: "pointer",
	fontFamily: "'BitHigh', sans-serif",
	fontSize: "12px",
	fontWeight: "700",
	textTransform: "uppercase",
	boxShadow: "0 0 10px rgba(255, 0, 127, 0.6)",
	transition: "background 0.3s ease-in-out, box-shadow 0.3s ease-in-out, opacity 0.3s ease-in-out",
	opacity: "0.9",
  });

  closeButton.addEventListener("mouseover", () => {
	closeButton.style.backgroundColor = "rgba(255, 51, 153, 0.95)";
	closeButton.style.boxShadow = "0 0 15px rgba(255, 51, 153, 1)";
	closeButton.style.opacity = "1";
  });
  closeButton.addEventListener("mouseout", () => {
	closeButton.style.backgroundColor = "rgba(255, 0, 0, 0.7)";
	closeButton.style.boxShadow = "0 0 10px rgba(255, 0, 127, 0.6)";
	closeButton.style.opacity = "0.9";
  });

// In showMoodAnalysisPopup (document 4), replace the closePopup function and handlers:
  
 const closePopup = () => {
   popupDiv.style.transform = "scale(0.9)";
   popupDiv.style.opacity = "0";
   setTimeout(() => {
	 backdrop.remove();
	 // Mood popup close no longer triggers redirect - redirect handled by timeout in stockTransaction.js
   }, 200);
 };


  
  closeButton.addEventListener("click", closePopup);
  
  backdrop.addEventListener("click", (e) => { 
	if (e.target === backdrop) closePopup(); 
  });
  
  document.addEventListener("keydown", function escHandler(ev) {
	if (ev.key === "Escape") { 
	  closePopup(); 
	  document.removeEventListener("keydown", escHandler); 
	}
  });

  footer.appendChild(closeButton);

  // Assemble
  popupDiv.appendChild(header);
  popupDiv.appendChild(contentContainer);
  popupDiv.appendChild(footer);
  backdrop.appendChild(popupDiv);
  document.body.appendChild(backdrop);

  // Show animation
  setTimeout(() => {
	popupDiv.style.transform = "scale(1)";
	popupDiv.style.opacity = "1";
  }, 50);

  // Prevent scroll
  const originalOverflow = document.body.style.overflow;
  document.body.style.overflow = "hidden";
  backdrop.addEventListener("transitionend", () => {
	document.body.style.overflow = originalOverflow;
  }, { once: true });
}



function generateFallbackMoodData() {
  return {
	userMood: {
	  averageMood: 0.65,
	  moodHistory: [
		{ positiveImpact: 0.8, negativeImpact: 0.2, timestamp: new Date().toISOString() },
		{ positiveImpact: 0.7, negativeImpact: 0.3, timestamp: new Date(Date.now() - 3600000).toISOString() }
	  ]
	},
	marketMood: {
	  averageMood: 0.43,
	  sentiment: { ratio: "1.5" },
	  transactionCount: 127
	}
  };
}

